/**
 * RestroPulse Loyalty — Admin surface (Phase 1 MVP, Slice 4).
 *
 * Tabs (per approved IA):
 *   1. Dashboard          — KPI snapshot
 *   2. Config             — per-outlet loyalty config (theme, slug, QR)
 *   3. Voucher Types      — catalog CRUD
 *   4. Kits               — bundle catalog CRUD (cross-outlet composition)
 *   5. Members            — search + create + kit purchase/renewal from member detail
 *   6. Alerts             — anomaly review queue
 *   7. Reconciliation     — redemption ledger + summary
 *
 * All API calls go through `${API}/admin/loyalty/…` with the auth headers.
 * The "Purchase" tab is intentionally NOT standalone — kit purchase &
 * renewal happen inside the Member detail dialog (per approved IA).
 */
import { useState, useEffect, useCallback, useMemo, createContext, useContext } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { LoyaltyPeriodFilterBar } from '../components/loyalty/LoyaltyPeriodFilterBar';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { Gift, Search, Plus, Trash2, Pencil, RefreshCw, AlertTriangle,
         CheckCircle2, CreditCard, Users, Package, ShieldAlert, ReceiptText,
         LayoutDashboard, Settings2, QrCode, Copy, Archive, Download, Sparkles,
         Paintbrush } from 'lucide-react';
import LightVoucherPreview from '../components/loyalty/LightVoucherPreview';
import DesignPickerModal from '../components/loyalty/DesignPickerModal';
import { Checkbox } from '../components/ui/checkbox';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Capability context so every child tab can decide whether to render
// admin-only mutation controls. Populated once at the module root from
// the authenticated user's role. Backend still enforces every write —
// this only controls the client UI.
const LoyaltyCapContext = createContext({ canEdit: true, isSales: false });
export const useLoyaltyCaps = () => useContext(LoyaltyCapContext);

const BENEFIT_TYPES = [
  { value: 'percent_discount', label: 'Percent Discount (%)' },
  { value: 'flat_discount', label: 'Flat Discount (₹)' },
  { value: 'complimentary_item', label: 'Complimentary Item' },
  { value: 'buy_one_get_one', label: 'Buy One Get One' },
  { value: 'cashback', label: 'Cashback' },
  { value: 'custom', label: 'Custom' },
];

const INR = (n) => (n == null ? '—' : `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`);

export default function AdminLoyalty() {
  const { user, getHeaders, API } = useAuth();
  const [outlets, setOutlets] = useState([]);
  const [sp, setSp] = useSearchParams();

  // Valid tab keys — keep in sync with the TabsList below.
  const VALID_TABS = ['dashboard','config','vouchers','kits','members',
                       'insights','alerts','reconciliation'];
  const urlTab      = sp.get('tab');
  const urlOutletId = sp.get('outlet_id') || '';

  // Persist-per-user last valid outlet. Never blindly resets to
  // outlets[0] — that is what caused the "Woodland Eatery Vapi" defect.
  const LS_KEY = `restropulse_loyalty_outlet::${user?.id || user?._id || 'anon'}`;
  const lastOutletFromStorage = (() => {
    try { return window.localStorage.getItem(LS_KEY) || ''; }
    catch { return ''; }
  })();

  const tab = VALID_TABS.includes(urlTab) ? urlTab : 'dashboard';
  const outletId = urlOutletId;   // may be '' until outlets load

  const canEdit = ['admin','area_manager','super_admin'].includes(user?.role);
  const isSales = user?.role === 'loyalty_sales';
  const caps = useMemo(() => ({ canEdit, isSales }), [canEdit, isSales]);

  // Setters — write to URL search params so the address bar becomes the
  // single source of truth. Preserve the other axis on every mutation.
  const setTab = useCallback((next) => {
    const nsp = new URLSearchParams(sp);
    nsp.set('tab', next);
    if (outletId) nsp.set('outlet_id', outletId);
    setSp(nsp, { replace: false });
  }, [sp, setSp, outletId]);

  const setOutletId = useCallback((next) => {
    const nsp = new URLSearchParams(sp);
    if (next) nsp.set('outlet_id', next);
    else nsp.delete('outlet_id');
    if (!nsp.get('tab')) nsp.set('tab', tab);
    setSp(nsp, { replace: false });
    try {
      if (next) window.localStorage.setItem(LS_KEY, next);
      else window.localStorage.removeItem(LS_KEY);
    } catch { /* ignore */ }
  }, [sp, setSp, tab, LS_KEY]);

  useEffect(() => {
    axios.get(`${API}/outlets`, { headers: getHeaders() })
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : (res.data.outlets || []);
        setOutlets(list);
        const ids = new Set(list.map((o) => o.id));

        // Priority: URL outlet (if authorised) > localStorage (if
        // authorised) > leave empty and prompt user. Never silently
        // pick outlets[0].
        let next = '';
        if (urlOutletId && ids.has(urlOutletId)) {
          next = urlOutletId;
        } else if (lastOutletFromStorage && ids.has(lastOutletFromStorage)) {
          next = lastOutletFromStorage;
        }
        // If the URL had an outlet_id but the user isn't authorised for
        // it any more, discard it (never retain stale cross-scope
        // outlet context).
        if (urlOutletId && !ids.has(urlOutletId)) {
          const nsp = new URLSearchParams(sp);
          nsp.delete('outlet_id');
          if (!nsp.get('tab')) nsp.set('tab', tab);
          setSp(nsp, { replace: true });
          try { window.localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
        }
        if (next && next !== urlOutletId) {
          const nsp = new URLSearchParams(sp);
          nsp.set('outlet_id', next);
          if (!nsp.get('tab')) nsp.set('tab', tab);
          setSp(nsp, { replace: true });
          try { window.localStorage.setItem(LS_KEY, next); } catch { /* ignore */ }
        }
      })
      .catch(() => toast.error('Failed to load outlets'));
  }, [API]);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <LoyaltyCapContext.Provider value={caps}>
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="admin-loyalty-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Gift className="w-6 h-6 text-[#C9A227]" />
            Loyalty Program
            {isSales && (
              <Badge variant="outline" className="ml-2 text-[10px] uppercase tracking-wider border-amber-300 text-amber-800 bg-amber-50">
                Sales · Read-only
              </Badge>
            )}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {isSales
              ? 'Read-only view of the loyalty catalogue. Sell approved memberships and vouchers to guests.'
              : 'Configure the voucher catalog, manage members, and reconcile redemptions.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-sm text-gray-600">Outlet:</Label>
          <Select value={outletId} onValueChange={setOutletId}>
            <SelectTrigger className="w-64" data-testid="loyalty-outlet-picker">
              <SelectValue placeholder="Select outlet" />
            </SelectTrigger>
            <SelectContent>
              {outlets.map((o) => (
                <SelectItem key={o.id} value={o.id}>
                  {o.name}{o.hierarchy === 'sub' ? ' (sub)' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full grid-cols-4 md:grid-cols-8" data-testid="loyalty-tabs">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">
            <LayoutDashboard className="w-4 h-4 mr-1" />Dashboard
          </TabsTrigger>
          <TabsTrigger value="config" data-testid="tab-config">
            <Settings2 className="w-4 h-4 mr-1" />Config
          </TabsTrigger>
          <TabsTrigger value="vouchers" data-testid="tab-vouchers">
            <Gift className="w-4 h-4 mr-1" />Voucher Types
          </TabsTrigger>
          <TabsTrigger value="kits" data-testid="tab-kits">
            <Package className="w-4 h-4 mr-1" />Memberships
          </TabsTrigger>
          <TabsTrigger value="members" data-testid="tab-members">
            <Users className="w-4 h-4 mr-1" />Members
          </TabsTrigger>
          <TabsTrigger value="insights" data-testid="tab-insights">
            <Sparkles className="w-4 h-4 mr-1" />Insights
          </TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts">
            <ShieldAlert className="w-4 h-4 mr-1" />Alerts
          </TabsTrigger>
          <TabsTrigger value="reconciliation" data-testid="tab-reconciliation">
            <ReceiptText className="w-4 h-4 mr-1" />Reconciliation
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard">
          <DashboardTab outletId={outletId} outlets={outlets} />
        </TabsContent>
        <TabsContent value="config">
          <ConfigTab outletId={outletId} />
        </TabsContent>
        <TabsContent value="vouchers">
          <VoucherTypesTab outletId={outletId} />
        </TabsContent>
        <TabsContent value="kits">
          <KitsTab outletId={outletId} outlets={outlets} />
        </TabsContent>
        <TabsContent value="members">
          <MembersTab outletId={outletId} outlets={outlets} />
        </TabsContent>
        <TabsContent value="insights">
          <InsightsTab outletId={outletId} outlets={outlets} />
        </TabsContent>
        <TabsContent value="alerts">
          <AlertsTab outletId={outletId} />
        </TabsContent>
        <TabsContent value="reconciliation">
          <ReconciliationTab outletId={outletId} outlets={outlets} />
        </TabsContent>
      </Tabs>
    </div>
    </LoyaltyCapContext.Provider>
  );
}

// ============================================================
//                      Dashboard tab
// ============================================================

function KpiCard({ label, value, hint, testid, tone = 'default' }) {
  const bg = {
    default: 'bg-white',
    good: 'bg-emerald-50',
    warn: 'bg-amber-50',
    bad: 'bg-rose-50',
  }[tone];
  return (
    <Card className={bg} data-testid={testid}>
      <CardContent className="p-4">
        <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
        <div className="text-2xl font-semibold text-gray-900 mt-1">{value}</div>
        {hint && <div className="text-xs text-gray-500 mt-1">{hint}</div>}
      </CardContent>
    </Card>
  );
}


/* ITER-B6 — Downloadable analytics reports (JSON / CSV / PDF). Fires
   an authenticated GET, converts the blob into a synthetic <a download>
   click so the browser saves without a page navigation. */
function ExportMenu({ API, getHeaders, outletId, periodParams }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState('');

  const doExport = async (fmt) => {
    setBusy(fmt);
    try {
      const params = new URLSearchParams(periodParams || {});
      params.set('format', fmt);
      if (outletId) params.append('outlet_id', outletId);
      const res = await axios.get(
        `${API}/admin/loyalty/analytics/export?${params.toString()}`,
        { headers: getHeaders(), responseType: 'blob' },
      );
      const mime = { json: 'application/json',
                     csv: 'text/csv',
                     pdf: 'application/pdf' }[fmt];
      const blob = new Blob([res.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `loyalty-analytics-${new Date().toISOString().slice(0, 10)}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${fmt.toUpperCase()} report`);
    } catch { toast.error('Export failed'); }
    finally { setBusy(''); setOpen(false); }
  };

  return (
    <div className="relative">
      <Button size="sm" variant="outline" onClick={() => setOpen((v) => !v)}
              data-testid="analytics-export-btn">
        <Download className="w-4 h-4 mr-1" />Export
      </Button>
      {open && (
        <div className="absolute right-0 mt-1 w-40 bg-white border rounded shadow-md z-30"
             data-testid="analytics-export-menu"
             onMouseLeave={() => setOpen(false)}>
          {['json', 'csv', 'pdf'].map((fmt) => (
            <button key={fmt}
                    onClick={() => doExport(fmt)}
                    disabled={!!busy}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
                    data-testid={`analytics-export-${fmt}`}>
              {busy === fmt ? '⏳ ' : ''}Download {fmt.toUpperCase()}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


function DashboardTab({ outletId, outlets }) {
  const { getHeaders, API } = useAuth();
  const { canEdit } = useLoyaltyCaps();
  const [scope, setScope] = useState('outlet');  // 'outlet' | 'tenant'
  const [kpi, setKpi] = useState(null);
  const [loading, setLoading] = useState(false);
  // ITER291 — Period filter state. Default 'current' → current-month
  // scope. `year`/`month` only used when preset === 'custom'; presets
  // themselves resolve the window on the backend.
  const now = new Date();
  const [period, setPeriod] = useState('current');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const setYm = (y, m) => { setYear(y); setMonth(m); };

  const periodParams = useMemo(() => {
    const p = new URLSearchParams();
    if (period) p.append('period', period);
    if (period === 'custom') {
      p.append('year', year);
      p.append('month', month);
    }
    return p;
  }, [period, year, month]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams(periodParams);
      if (scope === 'outlet' && outletId) p.append('outlet_id', outletId);
      const res = await axios.get(
        `${API}/admin/loyalty/dashboard?${p.toString()}`,
        { headers: getHeaders() },
      );
      setKpi(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [API, getHeaders, outletId, scope, periodParams]);

  useEffect(() => { load(); }, [load]);

  const scopeLabel = kpi?.period?.label || '';

  if (loading || !kpi) {
    return (
      <div className="space-y-6 mt-4" data-testid="loyalty-dashboard-loading">
        <LoyaltyPeriodFilterBar period={period} setPeriod={setPeriod}
                                 year={year} month={month} setYm={setYm} />
        <div className="p-8 text-center text-gray-500 text-sm">Loading…</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 mt-4" data-testid="loyalty-dashboard">
      <LoyaltyPeriodFilterBar period={period} setPeriod={setPeriod}
                               year={year} month={month} setYm={setYm}
                               scopeLabel={scopeLabel} />
      <div className="flex items-center gap-2">
        <div className="text-sm text-gray-600">Scope:</div>
        <div className="inline-flex rounded-md border">
          <button
            data-testid="scope-outlet"
            onClick={() => setScope('outlet')}
            className={`px-3 py-1 text-sm ${scope === 'outlet' ? 'bg-[color:var(--color-primary)] text-white' : ''}`}>
            This Outlet
          </button>
          <button
            data-testid="scope-tenant"
            onClick={() => setScope('tenant')}
            className={`px-3 py-1 text-sm ${scope === 'tenant' ? 'bg-[color:var(--color-primary)] text-white' : ''}`}>
            All Outlets
          </button>
        </div>
        <Button size="sm" variant="ghost" onClick={load} className="ml-auto">
          <RefreshCw className="w-4 h-4 mr-1" />Refresh
        </Button>
        {canEdit && (
          <ExportMenu API={API} getHeaders={getHeaders}
                      outletId={scope === 'outlet' ? outletId : null}
                      periodParams={periodParams} />
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Active Members" value={kpi.active_members} testid="kpi-active-members" tone="good" />
        <KpiCard label="Expired Members" value={kpi.expired_members} testid="kpi-expired-members" />
        <KpiCard label="Total Memberships" value={kpi.total_memberships} testid="kpi-total-memberships" />
        <KpiCard label="Expiring in 30 days" value={kpi.expiring_memberships_30d}
                 testid="kpi-expiring-30d"
                 tone={kpi.expiring_memberships_30d > 0 ? 'warn' : 'default'} />
      </div>

      {/* ITER290 — Member-driven revenue block. This is the PRIMARY
          revenue metric — the actual restaurant revenue generated by
          member visits at the till. Membership-sales revenue is shown
          separately below so operators can distinguish "revenue from
          selling memberships" vs "revenue from member visits". */}
      <Card data-testid="kpi-member-revenue-block">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Member Revenue</CardTitle>
          <div className="text-xs text-gray-500">
            Restaurant revenue generated through member visits (from
            bill values captured at redemption).
          </div>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <KpiCard label="Total Member Revenue"
                    value={INR(kpi.member_revenue)}
                    testid="kpi-member-revenue" tone="good" />
          <KpiCard label="Total Discount Given"
                    value={INR(kpi.total_discount_given)}
                    testid="kpi-total-discount"
                    tone={(kpi.total_discount_given || 0) > 0 ? 'warn' : 'default'} />
          <KpiCard label="Discount Cost %"
                    value={`${kpi.discount_cost_pct || 0}%`}
                    hint="Discount ÷ Member Revenue"
                    testid="kpi-discount-cost-pct"
                    tone={(kpi.discount_cost_pct || 0) > 15 ? 'warn' : 'default'} />
          <KpiCard label="Avg Bill Value (Members)"
                    value={INR(kpi.avg_bill_value_members)}
                    testid="kpi-avg-bill-value" />
          <KpiCard label="Avg Discount / Redemption"
                    value={INR(kpi.avg_discount_per_redemption)}
                    testid="kpi-avg-discount-per-redemption" />
          <KpiCard label="Redemption Count"
                    value={kpi.redemption_count}
                    testid="kpi-redemption-count" />
        </CardContent>
      </Card>

      <Card data-testid="kpi-membership-sales-block">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Membership Sales</CardTitle>
          <div className="text-xs text-gray-500">
            Revenue earned from selling membership plans (separate from
            member-generated restaurant revenue above).
          </div>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiCard label="Membership Sales Revenue"
                    value={INR(kpi.membership_sales_revenue ?? kpi.membership_revenue)}
                    testid="kpi-membership-sales-revenue" tone="good" />
          <KpiCard label="Sold Today" value={kpi.memberships_sold_today}
                    testid="kpi-sold-today" />
          <KpiCard label="Sold MTD" value={kpi.memberships_sold_mtd}
                    testid="kpi-sold-mtd" />
          <KpiCard label="Voucher Redemption %"
                    value={`${kpi.voucher_redemption_pct}%`}
                    testid="kpi-redemption-pct" />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <KpiCard label="Total Redemptions" value={kpi.total_redemptions} testid="kpi-total-redemptions" />
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Revenue by Membership</CardTitle></CardHeader>
        <CardContent>
          {kpi.revenue_by_kit.length === 0 ? (
            <div className="text-sm text-gray-500">No purchases yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-500 border-b">
                <tr><th className="text-left py-2">Membership</th><th className="text-right">Sold</th><th className="text-right">Revenue</th></tr>
              </thead>
              <tbody>
                {kpi.revenue_by_kit.map((r) => (
                  <tr key={r.kit_id} className="border-b last:border-b-0">
                    <td className="py-2">{r.kit_name || r.kit_code || '—'}</td>
                    <td className="text-right">{r.count}</td>
                    <td className="text-right font-medium">{INR(r.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Voucher Status Breakdown</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {Object.entries(kpi.voucher_status_breakdown || {}).map(([status, n]) => (
              <Badge key={status} variant="outline" data-testid={`voucher-status-${status}`}>
                {status}: {n}
              </Badge>
            ))}
            {Object.keys(kpi.voucher_status_breakdown || {}).length === 0 && (
              <span className="text-sm text-gray-500">No vouchers issued yet.</span>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================
//                        Config tab
// ============================================================

function ConfigTab({ outletId }) {
  const { getHeaders, API } = useAuth();
  const { canEdit } = useLoyaltyCaps();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [qr, setQr] = useState(null);

  const load = useCallback(async () => {
    if (!outletId) return;
    try {
      const [cfgRes, menuCfgRes] = await Promise.all([
        axios.get(`${API}/admin/loyalty/outlet-config/${outletId}`, { headers: getHeaders() }),
        axios.get(`${API}/admin/outlets/${outletId}/menu-config`, { headers: getHeaders() })
          .catch(() => ({ data: { menu_enabled: false } })),
      ]);
      // iter280 — annotate cfg with menu-module state so the Menu
      // Studio hint link can be conditionally rendered. Underscore
      // prefix keeps these client-only (never persisted).
      setCfg({
        ...cfgRes.data,
        _outlet_id: outletId,
        _menu_module_enabled: !!menuCfgRes.data?.menu_enabled,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load config');
    }
  }, [API, getHeaders, outletId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        enabled: cfg.enabled,
        program_name: cfg.program_name,
        theme_primary: cfg.theme_primary,
        theme_secondary: cfg.theme_secondary,
        landing_url_slug: cfg.landing_url_slug,
        contact_email: cfg.contact_email,
        welcome_message: cfg.welcome_message,
        terms_url: cfg.terms_url,
      };
      if (cfg._logo_pending) {
        patch.logo_base64 = cfg._logo_pending.base64;
        patch.logo_content_type = cfg._logo_pending.type;
        patch.logo_filename = cfg._logo_pending.name;
      }
      const res = await axios.patch(`${API}/admin/loyalty/outlet-config/${outletId}`, patch, { headers: getHeaders() });
      setCfg({
        ...res.data,
        _logo_pending: undefined,
        _outlet_id: outletId,
        _menu_module_enabled: cfg._menu_module_enabled,
      });
      toast.success('Config saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const loadQr = async (target) => {
    try {
      const res = await axios.get(`${API}/admin/loyalty/qr/${target}?outlet_id=${outletId}`, { headers: getHeaders() });
      setQr({ target, ...res.data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'QR failed');
    }
  };

  if (!cfg) return <div className="p-6 text-gray-500">Loading…</div>;

  // For loyalty_sales: filtered read-only subset (name / colors /
  // welcome). Hides tokens, logo upload, QR codes and any file/secret
  // controls. Everything below is INFORMATIONAL only.
  if (!canEdit) {
    return (
      <div className="mt-4 space-y-4" data-testid="loyalty-config-readonly">
        <Card>
          <CardHeader><CardTitle className="text-base">Program Settings (read-only)</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <ReadRow label="Enabled"       value={cfg.enabled ? 'Yes' : 'No'} />
            <ReadRow label="Program Name"  value={cfg.program_name || '—'} />
            <ReadRow label="Landing Slug"  value={cfg.landing_url_slug || '—'} />
            <ReadRow label="Primary"       value={cfg.theme_primary || '—'} />
            <ReadRow label="Secondary"     value={cfg.theme_secondary || '—'} />
            <ReadRow label="Contact Email" value={cfg.contact_email || '—'} />
            <ReadRow label="Welcome Message" value={cfg.welcome_message || '—'} />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4" data-testid="loyalty-config">
      <Card>
        <CardHeader><CardTitle className="text-base">Program Settings</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>Enabled</Label>
            <Switch checked={!!cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })}
                    data-testid="config-enabled-switch" />
          </div>
          <Field label="Program Name">
            <Input value={cfg.program_name || ''} onChange={(e) => setCfg({ ...cfg, program_name: e.target.value })}
                   data-testid="config-program-name" />
          </Field>
          <Field label="Landing URL Slug">
            <Input value={cfg.landing_url_slug || ''} onChange={(e) => setCfg({ ...cfg, landing_url_slug: e.target.value })}
                   data-testid="config-slug"
                   placeholder="3-64 chars, lowercase alnum + dashes" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Primary Color">
              <Input value={cfg.theme_primary || ''} onChange={(e) => setCfg({ ...cfg, theme_primary: e.target.value })}
                     placeholder="var(--color-primary)" />
            </Field>
            <Field label="Secondary Color">
              <Input value={cfg.theme_secondary || ''} onChange={(e) => setCfg({ ...cfg, theme_secondary: e.target.value })}
                     placeholder="#C9A227" />
            </Field>
          </div>
          <Field label="Contact Email">
            <Input value={cfg.contact_email || ''} onChange={(e) => setCfg({ ...cfg, contact_email: e.target.value })} />
          </Field>
          <Field label="Welcome Message">
            <Textarea rows={3} value={cfg.welcome_message || ''}
                      onChange={(e) => setCfg({ ...cfg, welcome_message: e.target.value })} />
          </Field>
          {/* iter280 — Menu-scoped welcome message removed from Loyalty
              admin. Source of truth is now Menu Studio (per-outlet). */}
          {cfg._menu_module_enabled && cfg._outlet_id && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-xs text-emerald-800 flex items-center justify-between"
                 data-testid="menu-studio-hint">
              <span>
                Looking for the digital menu welcome message? It moved to
                Menu Studio.
              </span>
              <Link to={`/admin/outlets/${cfg._outlet_id}/menus`}
                    className="font-semibold text-emerald-700 hover:text-emerald-900 ml-3 whitespace-nowrap"
                    data-testid="menu-studio-link">
                Menu Studio →
              </Link>
            </div>
          )}
          <Field label="Terms URL">
            <Input value={cfg.terms_url || ''} onChange={(e) => setCfg({ ...cfg, terms_url: e.target.value })} />
          </Field>

          {/* Outlet Logo — used inline in OTP + welcome emails (CID). */}
          <div className="space-y-2">
            <Label>Outlet Logo</Label>
            {!cfg.logo_base64 && !cfg._logo_pending && (
              <div className="text-xs px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-amber-800"
                   data-testid="logo-empty-warning">
                Logo not set — will not appear in welcome or OTP emails.
              </div>
            )}
            <div className="flex items-center gap-3">
              {(cfg._logo_pending || cfg.logo_base64) && (
                <img
                  src={cfg._logo_pending
                    ? `data:${cfg._logo_pending.type};base64,${cfg._logo_pending.base64}`
                    : `data:${cfg.logo_content_type || 'image/png'};base64,${cfg.logo_base64}`}
                  alt="logo" className="w-14 h-14 rounded-lg border object-contain bg-white"
                  data-testid="logo-preview"
                />
              )}
              <label className="cursor-pointer">
                <input type="file" accept="image/png,image/jpeg" className="hidden"
                       data-testid="logo-upload-input"
                       onChange={(e) => {
                         const f = e.target.files?.[0];
                         if (!f) return;
                         if (f.size > 500 * 1024) {
                           toast.error('Logo must be under 500KB');
                           return;
                         }
                         if (!/image\/(png|jpe?g)/i.test(f.type)) {
                           toast.error('Only PNG or JPG allowed');
                           return;
                         }
                         const reader = new FileReader();
                         reader.onload = () => {
                           const b64 = String(reader.result || '').split(',')[1] || '';
                           setCfg({
                             ...cfg,
                             _logo_pending: {
                               base64: b64, type: f.type, name: f.name,
                             },
                           });
                         };
                         reader.readAsDataURL(f);
                       }} />
                <span className="inline-flex items-center gap-1 rounded-md border px-3 h-9 text-sm hover:bg-gray-50">
                  {cfg.logo_base64 || cfg._logo_pending ? 'Replace Logo' : 'Upload Logo'}
                </span>
              </label>
              {(cfg.logo_base64 || cfg._logo_pending) && (
                <Button variant="ghost" size="sm"
                        onClick={() => setCfg({ ...cfg, _logo_pending: { base64: '', type: '', name: '' } })}
                        data-testid="logo-remove-btn">
                  Remove
                </Button>
              )}
            </div>
            <div className="text-[11px] text-gray-500">
              PNG or JPG, ≤500KB. Displayed at the top of every OTP + welcome email.
            </div>
          </div>

          <Button onClick={save} disabled={saving} data-testid="config-save">
            {saving ? 'Saving…' : 'Save Config'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><QrCode className="w-4 h-4" />QR Codes</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-gray-500">
            Generate printable QR codes for the outlet-themed guest landing page and
            manager redemption terminal.
          </p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => loadQr('guest')} data-testid="qr-guest-btn">
              Guest QR
            </Button>
            <Button size="sm" variant="outline" onClick={() => loadQr('manager')} data-testid="qr-manager-btn">
              Manager QR
            </Button>
          </div>
          {qr && (
            <div className="space-y-2 pt-2 border-t">
              <div className="text-xs text-gray-500">Target: <span className="font-medium">{qr.target}</span></div>
              <img src={qr.qr_png_data_url} alt="Loyalty QR" className="w-48 h-48 border" data-testid="qr-image" />
              <div className="flex items-center gap-2 text-xs">
                <code className="bg-gray-100 px-2 py-1 rounded break-all flex-1">{qr.landing_url}</code>
                <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(qr.landing_url); toast.success('URL copied'); }}>
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-gray-600 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

function ReadRow({ label, value }) {
  return (
    <div className="flex items-baseline justify-between border-b last:border-b-0 py-1.5">
      <span className="text-xs uppercase tracking-wider text-slate-500">{label}</span>
      <span className="text-sm text-slate-800 font-medium max-w-[60%] text-right break-words">{value}</span>
    </div>
  );
}

// ============================================================
//                    Voucher Types tab
// ============================================================

function VoucherTypesTab({ outletId }) {
  const { getHeaders, API } = useAuth();
  const { canEdit } = useLoyaltyCaps();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);   // null | 'new' | {row}
  const [includeInactive, setIncludeInactive] = useState(false);
  // ITER302 · outlet identity for the thumbnail preview (logo + name).
  const [outletConfig, setOutletConfig] = useState(null);
  const [outletName, setOutletName] = useState('');
  // ITER303.1 · multi-select + design picker state.
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [pickerMode, setPickerMode] = useState(null);  // null | 'bulk' | 'row'
  const [quickApplyRowId, setQuickApplyRowId] = useState(null);

  const load = useCallback(async () => {
    if (!outletId) return;
    setLoading(true);
    try {
      const [rowsRes, cfgRes, outletRes] = await Promise.all([
        axios.get(`${API}/admin/loyalty/voucher-types?outlet_id=${outletId}&include_inactive=${includeInactive}`,
                  { headers: getHeaders() }),
        axios.get(`${API}/admin/loyalty/outlet-config/${outletId}`, { headers: getHeaders() })
             .catch(() => ({ data: null })),
        axios.get(`${API}/outlets`, { headers: getHeaders() }).catch(() => ({ data: [] })),
      ]);
      setRows(rowsRes.data);
      setOutletConfig(cfgRes.data);
      const list = Array.isArray(outletRes.data) ? outletRes.data : (outletRes.data?.outlets || []);
      const outlet = list.find((o) => o.id === outletId);
      setOutletName(outlet?.name || '');
    } catch { toast.error('Failed to load voucher types'); }
    finally { setLoading(false); }
  }, [API, getHeaders, outletId, includeInactive]);

  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm('Delete this voucher type? If it has been issued, it will be deactivated instead.')) return;
    try {
      await axios.delete(`${API}/admin/loyalty/voucher-types/${id}`, { headers: getHeaders() });
      toast.success('Deleted');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  // ── ITER303.1 · Selection helpers ────────────────────────────────
  const toggleRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelectedIds((prev) => {
      if (prev.size === rows.length) return new Set();
      return new Set(rows.map((r) => r.id));
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const applyDesignToIds = async (design, ids) => {
    if (!design || !ids?.length) return;
    try {
      const res = await axios.post(
        `${API}/admin/loyalty/voucher-types/bulk-apply-design`,
        { voucher_type_ids: ids, design },
        { headers: getHeaders() },
      );
      toast.success(`Design applied to ${res.data.modified} voucher${res.data.modified === 1 ? '' : 's'}`);
      clearSelection();
      setQuickApplyRowId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk apply failed');
    }
  };

  const onPickerConfirm = (design) => {
    if (pickerMode === 'row' && quickApplyRowId) {
      applyDesignToIds(design, [quickApplyRowId]);
    } else if (pickerMode === 'bulk') {
      applyDesignToIds(design, Array.from(selectedIds));
    }
    setPickerMode(null);
  };

  const outletLogo = outletConfig?.logo_base64
    ? `data:${outletConfig.logo_content_type || 'image/png'};base64,${outletConfig.logo_base64}`
    : null;
  const outletInitials = (outletConfig?.program_name || outletName || 'RP')
    .split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="space-y-4 mt-4" data-testid="voucher-types-tab">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <Label className="text-sm">Show inactive</Label>
          <Switch checked={includeInactive} onCheckedChange={setIncludeInactive} />
          {/* Phase-1 closeout · bulk "Apply Design" action bar retired.
              Legacy handler kept below for the (dormant) DesignPickerModal
              but no visible caller remains in normal admin navigation. */}
        </div>
        {canEdit && (
        <div className="flex items-center gap-2">
          {/* Phase 1 · The old "Open Studio" and per-row Paintbrush /
              Sparkles entry points were removed from normal admin
              navigation. Voucher creation and design editing now flow
              through the single canonical Voucher Builder. The Studio
              route + Quick-Apply picker remain routed for compatibility
              during rollout but are no longer visible here. */}
          <Button onClick={() => nav(`/admin/loyalty/voucher-builder?outlet_id=${outletId || ''}&from=vouchers`)}
                  data-testid="vt-new-btn">
            <Plus className="w-4 h-4 mr-1" />New Voucher
          </Button>
        </div>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500 border-b">
              <tr>
                {canEdit && (
                <th className="p-3 w-10">
                  <Checkbox
                    checked={rows.length > 0 && selectedIds.size === rows.length}
                    onCheckedChange={toggleAll}
                    data-testid="vt-select-all"
                    aria-label="Select all"
                  />
                </th>
                )}
                <th className="p-3 w-24">Preview</th>
                <th className="text-left p-3">Code</th>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Benefit</th>
                <th className="text-right p-3">Validity</th>
                <th className="text-center p-3">Active</th>
                {canEdit && <th className="p-3"></th>}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr><td colSpan={canEdit ? 8 : 6} className="p-6 text-center text-gray-400">No voucher types yet.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-b last:border-b-0 hover:bg-gray-50">
                  {canEdit && (
                  <td className="p-3">
                    <Checkbox
                      checked={selectedIds.has(r.id)}
                      onCheckedChange={() => toggleRow(r.id)}
                      data-testid={`vt-select-${r.id}`}
                      aria-label={`Select ${r.name}`}
                    />
                  </td>
                  )}
                  <td className="p-2">
                    {/* ITER305 · Lightweight CSS-only preview.
                        Replaces the full <VoucherCard> (QR SVG +
                        ribbon SVG + pattern SVG + container query +
                        per-card font side-effect) that froze the tab
                        with 67+ rows. */}
                    <LightVoucherPreview voucher={r} />
                  </td>
                  <td className="p-3 font-mono text-xs">{r.code}</td>
                  <td className="p-3">
                    {r.name}
                  </td>
                  <td className="p-3">
                    <Badge variant="outline">{r.benefit_type}</Badge>
                    {r.benefit_value != null && <span className="ml-2 text-gray-600">{r.benefit_value}</span>}
                  </td>
                  <td className="p-3 text-right">{r.validity_days}d</td>
                  <td className="p-3 text-center">
                    {r.active ? <CheckCircle2 className="w-4 h-4 text-emerald-600 inline" /> : <span className="text-gray-400">off</span>}
                    {r.sales_enabled && (
                      <div className="mt-1">
                        <Badge variant="outline"
                               className="text-[10px] px-1.5 py-0 border-amber-300 text-amber-700 bg-amber-50"
                               data-testid={`vt-sales-badge-${r.id}`}>
                          Sales
                        </Badge>
                      </div>
                    )}
                  </td>
                  <td className="p-3 text-right space-x-1 whitespace-nowrap">
                    {canEdit && (<>
                    {/* Phase 1 · Voucher Types row exposes only meaningful
                        business actions. Design editing goes through Edit
                        → Voucher Builder. Paintbrush (Quick Apply) and
                        Sparkles (Design in Studio) were removed from
                        normal navigation; template selection now happens
                        INSIDE the Builder. */}
                    <Button size="sm" variant="ghost"
                             onClick={() => nav(`/admin/loyalty/voucher-builder/${r.id}?outlet_id=${outletId || ''}&from=vouchers`)}
                             data-testid={`vt-edit-${r.id}`}
                             title="Edit voucher">
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => del(r.id)}><Trash2 className="w-3 h-3" /></Button>
                    </>)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {editing && (
        <VoucherTypeEditor
          value={editing === 'new' ? null : editing}
          outletId={outletId}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {/* ITER303.1 · Design picker (bulk + quick apply) */}
      <DesignPickerModal
        open={pickerMode !== null}
        onOpenChange={(v) => { if (!v) { setPickerMode(null); setQuickApplyRowId(null); } }}
        onPick={onPickerConfirm}
        headers={getHeaders()}
        outletName={outletConfig?.program_name || outletName || 'Restaurant'}
        outletLogo={outletLogo}
        outletInitials={outletInitials}
        title={pickerMode === 'bulk'
          ? `Apply design to ${selectedIds.size} voucher${selectedIds.size === 1 ? '' : 's'}`
          : 'Quick apply saved design'}
        description="This will overwrite the design on the selected voucher(s). Basic Details (name, value, T&Cs) are untouched."
        confirmLabel="Apply Design"
      />
    </div>
  );
}

function VoucherTypeEditor({ value, outletId, onClose, onSaved }) {
  const { getHeaders, API } = useAuth();
  const isNew = !value;
  const [form, setForm] = useState(value || {
    code: '', name: '', benefit_type: 'complimentary_item', benefit_value: '',
    validity_days: 365, description: '', active: true,
    hero_image_base64: '', hero_image_content_type: '', hero_image_filename: '',
  });
  const [saving, setSaving] = useState(false);
  const [heroError, setHeroError] = useState('');

  // ITER294 — Hero image upload. Client-side compress: max 1200 px on
  // longest edge, JPEG 82. Keeps the guest-wallet payload lean and the
  // Mongo doc under Atlas's 16MB cap even for un-optimised phone photos.
  const onHeroPick = async (file) => {
    setHeroError('');
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      setHeroError('Only image files are supported'); return;
    }
    try {
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result); r.onerror = () => rej(r.error);
        r.readAsDataURL(file);
      });
      const img = await new Promise((res, rej) => {
        const i = new Image();
        i.onload = () => res(i); i.onerror = () => rej(new Error('bad image'));
        i.src = dataUrl;
      });
      const maxEdge = 1200;
      const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      const jpeg = canvas.toDataURL('image/jpeg', 0.82);
      const b64 = jpeg.split(',')[1] || '';
      setForm((f) => ({ ...f,
        hero_image_base64: b64,
        hero_image_content_type: 'image/jpeg',
        hero_image_filename: file.name || 'hero.jpg',
      }));
    } catch (err) {
      setHeroError('Could not read this image. Try another file.');
    }
  };

  const clearHero = () => setForm((f) => ({ ...f,
    hero_image_base64: '',
    hero_image_content_type: '',
    hero_image_filename: '',
  }));

  const heroPreviewUrl = (() => {
    if (!form.hero_image_base64) return '';
    const ct = form.hero_image_content_type || 'image/jpeg';
    return `data:${ct};base64,${form.hero_image_base64}`;
  })();

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...(isNew ? { outlet_id: outletId } : {}),
        code: form.code.toUpperCase(),
        name: form.name,
        benefit_type: form.benefit_type,
        benefit_value: form.benefit_value === '' || form.benefit_value == null ? null : Number(form.benefit_value),
        validity_days: Number(form.validity_days),
        description: form.description,
        active: !!form.active,
        // ITER294 — send hero image alongside the existing fields. Empty
        // string clears an existing image (patch flow).
        hero_image_base64: form.hero_image_base64 || '',
        hero_image_content_type: form.hero_image_content_type || '',
        hero_image_filename: form.hero_image_filename || '',
      };
      if (isNew) {
        await axios.post(`${API}/admin/loyalty/voucher-types`, payload, { headers: getHeaders() });
      } else {
        await axios.patch(`${API}/admin/loyalty/voucher-types/${value.id}`, payload, { headers: getHeaders() });
      }
      toast.success('Saved');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="voucher-type-editor">
        <DialogHeader>
          <DialogTitle>{isNew ? 'New Voucher Type' : 'Edit Voucher Type'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Code">
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
                     placeholder="DESSERT-01" data-testid="vt-code" />
            </Field>
            <Field label="Validity (days)">
              <Input type="number" min={1} max={3650} value={form.validity_days}
                     onChange={(e) => setForm({ ...form, validity_days: e.target.value })} />
            </Field>
          </div>
          <Field label="Name">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   data-testid="vt-name" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Benefit Type">
              <Select value={form.benefit_type} onValueChange={(v) => setForm({ ...form, benefit_type: v })}>
                <SelectTrigger data-testid="vt-benefit-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BENEFIT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Benefit Value (optional)">
              <Input value={form.benefit_value ?? ''} onChange={(e) => setForm({ ...form, benefit_value: e.target.value })}
                     placeholder="10 or 200" />
            </Field>
          </div>
          <Field label="Description">
            <Textarea rows={2} value={form.description || ''}
                      onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </Field>

          {/* ITER294 — Hero image picker */}
          <Field label="Hero Image (optional)">
            <div className="space-y-2">
              {heroPreviewUrl ? (
                <div className="relative w-full aspect-[16/9] rounded-lg overflow-hidden border">
                  <img src={heroPreviewUrl} alt="Hero"
                       className="w-full h-full object-cover"
                       data-testid="vt-hero-preview" />
                  <button type="button" onClick={clearHero}
                          data-testid="vt-hero-clear"
                          className="absolute top-2 right-2 px-2.5 py-1 text-xs rounded-md
                                     bg-black/70 text-white hover:bg-black">
                    Remove
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full aspect-[16/9]
                                  border border-dashed rounded-lg cursor-pointer hover:bg-gray-50
                                  text-center text-sm text-gray-500"
                        data-testid="vt-hero-dropzone">
                  <div className="font-medium text-gray-700">Upload hero image</div>
                  <div className="text-xs mt-1">Food · Cocktail · Dessert · Experience · Room · Spa</div>
                  <div className="text-[11px] mt-2">Auto-optimised · 16:9 crop preview</div>
                  <input type="file" accept="image/*" className="sr-only"
                         onChange={(e) => onHeroPick(e.target.files?.[0])}
                         data-testid="vt-hero-input" />
                </label>
              )}
              {heroError && <div className="text-xs text-rose-600">{heroError}</div>}
              <div className="text-[11px] text-gray-500">
                If no image is uploaded, the voucher renders with an elegant brand-themed
                artwork automatically. You can change or remove this at any time.
              </div>
            </div>
          </Field>

          <div className="flex items-center gap-2">
            <Switch checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            <Label>Active</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="vt-save">
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
//                          Kits tab
// ============================================================

function KitsTab({ outletId, outlets }) {
  const { getHeaders, API } = useAuth();
  const { canEdit } = useLoyaltyCaps();
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(false);

  const load = useCallback(async () => {
    if (!outletId) return;
    try {
      const res = await axios.get(`${API}/admin/loyalty/kits?outlet_id=${outletId}&include_inactive=${includeInactive}`,
        { headers: getHeaders() });
      setRows(res.data);
    } catch { toast.error('Failed to load memberships'); }
  }, [API, getHeaders, outletId, includeInactive]);

  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm('Delete this membership? If already sold, it will be deactivated instead.')) return;
    try {
      await axios.delete(`${API}/admin/loyalty/kits/${id}`, { headers: getHeaders() });
      toast.success('Deleted');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="space-y-4 mt-4" data-testid="kits-tab">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <Label className="text-sm">Show inactive</Label>
          <Switch checked={includeInactive} onCheckedChange={setIncludeInactive} />
        </div>
        {canEdit && (
        <Button onClick={() => setEditing('new')} data-testid="kit-new-btn">
          <Plus className="w-4 h-4 mr-1" />New Membership
        </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500 border-b">
              <tr>
                <th className="text-left p-3">Code</th>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Tier</th>
                <th className="text-right p-3">Price</th>
                <th className="text-right p-3">Vouchers</th>
                <th className="text-center p-3">Active</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={7} className="p-6 text-center text-gray-400">No memberships yet.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-b last:border-b-0 hover:bg-gray-50">
                  <td className="p-3 font-mono text-xs">{r.code}</td>
                  <td className="p-3">{r.name}</td>
                  <td className="p-3">
                    {r.tier ? (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-300"
                            data-testid={`kit-tier-cell-${r.id}`}>
                        {r.tier}
                      </span>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="p-3 text-right font-medium">{INR(r.price)}</td>
                  <td className="p-3 text-right">
                    {(r.voucher_composition || []).reduce((s, l) => s + Number(l.count || 0), 0)}
                  </td>
                  <td className="p-3 text-center">
                    {r.active ? <CheckCircle2 className="w-4 h-4 text-emerald-600 inline" /> : <span className="text-gray-400">off</span>}
                    {r.sales_enabled && (
                      <div className="mt-1">
                        <Badge variant="outline"
                               className="text-[10px] px-1.5 py-0 border-amber-300 text-amber-700 bg-amber-50"
                               data-testid={`kit-sales-badge-${r.id}`}>
                          Sales
                        </Badge>
                      </div>
                    )}
                  </td>
                  <td className="p-3 text-right space-x-1">
                    {canEdit && (<>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(r)}><Pencil className="w-3 h-3" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => del(r.id)}><Trash2 className="w-3 h-3" /></Button>
                    </>)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {editing && (
        <KitEditor
          value={editing === 'new' ? null : editing}
          outletId={outletId}
          outlets={outlets}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function KitEditor({ value, outletId, outlets, onClose, onSaved }) {
  const { getHeaders, API } = useAuth();
  const isNew = !value;
  const [form, setForm] = useState(value || {
    code: '', name: '', price: 0, currency: 'INR', validity_days: 365,
    description: '', active: true, voucher_composition: [], tier: '',
    sales_enabled: false,
    terms_and_conditions: '',
  });
  const [saving, setSaving] = useState(false);
  const [vtCache, setVtCache] = useState({});  // outlet_id → voucher types

  const loadVtFor = useCallback(async (id) => {
    if (vtCache[id]) return vtCache[id];
    try {
      const res = await axios.get(`${API}/admin/loyalty/voucher-types?outlet_id=${id}`, { headers: getHeaders() });
      setVtCache((c) => ({ ...c, [id]: res.data }));
      return res.data;
    } catch { return []; }
  }, [API, getHeaders, vtCache]);

  useEffect(() => { loadVtFor(outletId); }, [outletId, loadVtFor]);

  const addLine = () => {
    setForm((f) => ({
      ...f,
      voucher_composition: [...(f.voucher_composition || []), { voucher_type_id: '', count: 1, source_outlet_id: outletId }],
    }));
  };
  const updateLine = (idx, patch) => {
    setForm((f) => ({
      ...f,
      voucher_composition: f.voucher_composition.map((l, i) => (i === idx ? { ...l, ...patch } : l)),
    }));
  };
  const removeLine = (idx) => {
    setForm((f) => ({
      ...f,
      voucher_composition: f.voucher_composition.filter((_, i) => i !== idx),
    }));
  };

  const save = async () => {
    if (!form.voucher_composition.length) {
      toast.error('Add at least one voucher line');
      return;
    }
    for (const l of form.voucher_composition) {
      if (!l.voucher_type_id) { toast.error('Pick a voucher type for every line'); return; }
    }
    setSaving(true);
    try {
      const payload = {
        ...(isNew ? { outlet_id: outletId } : {}),
        code: form.code.toUpperCase(),
        name: form.name,
        price: Number(form.price),
        currency: form.currency || 'INR',
        validity_days: Number(form.validity_days),
        description: form.description,
        active: !!form.active,
        tier: form.tier || null,
        sales_enabled: !!form.sales_enabled,
        terms_and_conditions: form.terms_and_conditions || '',
        voucher_composition: form.voucher_composition.map((l) => ({
          voucher_type_id: l.voucher_type_id,
          count: Number(l.count),
          source_outlet_id: l.source_outlet_id || outletId,
        })),
      };
      if (isNew) {
        await axios.post(`${API}/admin/loyalty/kits`, payload, { headers: getHeaders() });
      } else {
        await axios.patch(`${API}/admin/loyalty/kits/${value.id}`, payload, { headers: getHeaders() });
      }
      toast.success('Saved');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const TIERS = ['Bronze', 'Silver', 'Gold', 'Platinum'];

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="kit-editor">
        <DialogHeader>
          <DialogTitle>{isNew ? 'New Membership' : 'Edit Membership'}</DialogTitle>
          <DialogDescription>
            Memberships bundle voucher types into a purchasable pack. Voucher
            composition can reference vouchers from other outlets in the same
            tenant (cross-outlet).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <Field label="Code"><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="kit-code" /></Field>
            <Field label="Price"><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="kit-price" /></Field>
            <Field label="Validity (days)"><Input type="number" value={form.validity_days} onChange={(e) => setForm({ ...form, validity_days: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="kit-name" /></Field>
            <Field label="Tier">
              <div className="flex gap-1 flex-wrap" data-testid="kit-tier">
                {TIERS.map((t) => {
                  const active = form.tier === t;
                  return (
                    <button key={t} type="button"
                            onClick={() => setForm({ ...form, tier: active ? '' : t })}
                            className={`text-xs px-2.5 py-1.5 rounded-full border transition ${
                              active ? 'bg-slate-900 text-white border-slate-900'
                                     : 'bg-white text-gray-700 border-gray-300 hover:border-slate-500'
                            }`}
                            data-testid={`kit-tier-${t.toLowerCase()}`}>
                      {t}
                    </button>
                  );
                })}
              </div>
            </Field>
          </div>
          <Field label="Description"><Textarea rows={2} value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>

          <div className="border-t pt-3 space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Voucher Composition</Label>
              <Button size="sm" variant="outline" onClick={addLine} data-testid="kit-add-line">
                <Plus className="w-3 h-3 mr-1" />Add line
              </Button>
            </div>
            {form.voucher_composition.map((l, idx) => (
              <KitLineRow
                key={idx} idx={idx} line={l} outlets={outlets}
                vtCache={vtCache} loadVtFor={loadVtFor}
                onChange={(p) => updateLine(idx, p)}
                onRemove={() => removeLine(idx)}
              />
            ))}
            {form.voucher_composition.length === 0 && (
              <div className="text-xs text-gray-500 p-2 bg-gray-50 rounded">Add at least one voucher line.</div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Switch checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            <Label>Active</Label>
          </div>
          <div className="flex items-center justify-between border-t pt-3">
            <div>
              <Label className="text-sm">Available to Sales Team</Label>
              <div className="text-[11px] text-gray-500 mt-0.5">
                When enabled, this membership kit is available in the Loyalty
                Sales catalogue.
              </div>
            </div>
            <Switch checked={!!form.sales_enabled}
                    onCheckedChange={(v) => setForm({ ...form, sales_enabled: v })}
                    data-testid="kit-sales-enabled" />
          </div>
          <div className="border-t pt-3">
            <Label className="text-sm">Terms &amp; Conditions</Label>
            <div className="text-[11px] text-gray-500 mb-1">
              These terms are snapshotted onto every membership purchased
              from this kit. Editing here affects only future purchases.
            </div>
            <Textarea rows={6}
                      value={form.terms_and_conditions || ''}
                      onChange={(e) => setForm({ ...form, terms_and_conditions: e.target.value })}
                      placeholder={"1. Membership is valid for 12 months from activation.\n2. Benefits cannot be exchanged for cash.\n3. Blackout dates may apply."}
                      data-testid="kit-terms" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="kit-save">{saving ? 'Saving…' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KitLineRow({ line, outlets, vtCache, loadVtFor, onChange, onRemove }) {
  const src = line.source_outlet_id;
  useEffect(() => { if (src) loadVtFor(src); }, [src, loadVtFor]);
  const vts = vtCache[src] || [];
  return (
    <div className="grid grid-cols-12 gap-2 items-center">
      <div className="col-span-4">
        <Select value={src || ''} onValueChange={(v) => onChange({ source_outlet_id: v, voucher_type_id: '' })}>
          <SelectTrigger className="h-9"><SelectValue placeholder="Source outlet" /></SelectTrigger>
          <SelectContent>
            {outlets.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-5">
        <Select value={line.voucher_type_id || ''} onValueChange={(v) => onChange({ voucher_type_id: v })}>
          <SelectTrigger className="h-9"><SelectValue placeholder="Voucher type" /></SelectTrigger>
          <SelectContent>
            {vts.length === 0 && <div className="p-2 text-xs text-gray-400">No voucher types yet in this outlet.</div>}
            {vts.map((v) => <SelectItem key={v.id} value={v.id}>{v.name} ({v.code})</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-2">
        <Input type="number" min={1} value={line.count} onChange={(e) => onChange({ count: e.target.value })} />
      </div>
      <div className="col-span-1 text-right">
        <Button size="sm" variant="ghost" onClick={onRemove}><Trash2 className="w-3 h-3" /></Button>
      </div>
    </div>
  );
}

// ============================================================
//                        Insights tab
// ============================================================

function InsightsTab({ outletId, outlets }) {
  const { getHeaders, API } = useAuth();
  const [loading, setLoading] = useState(true);
  const [segments, setSegments] = useState([]);
  const [thresholds, setThresholds] = useState(null);
  const [openSegment, setOpenSegment] = useState(null);
  const [openMemberId, setOpenMemberId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = outletId
        ? `${API}/admin/loyalty/insights?outlet_id=${outletId}`
        : `${API}/admin/loyalty/insights`;
      const res = await axios.get(url, { headers: getHeaders() });
      setSegments(res.data?.segments || []);
      setThresholds(res.data?.thresholds || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load insights');
    } finally { setLoading(false); }
  }, [API, getHeaders, outletId]);

  useEffect(() => { load(); }, [load]);

  const active = segments.find((s) => s.key === openSegment) || null;

  return (
    <div className="space-y-4" data-testid="insights-panel">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-base font-semibold flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-600" /> Member Insights
          </h3>
          <p className="text-xs text-gray-500">
            Actionable CRM segments — click any card to see the members and act on them.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={load} data-testid="insights-refresh">
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500 py-8 text-center">Loading insights…</div>
      ) : segments.length === 0 ? (
        <div className="text-sm text-gray-500 py-8 text-center">No data yet.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
          {segments.map((s) => (
            <button
              key={s.key}
              onClick={() => setOpenSegment(s.key)}
              disabled={s.count === 0}
              data-testid={`insight-card-${s.key}`}
              className={`text-left border rounded-lg p-3 bg-white hover:shadow-md transition-shadow
                          disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none`}
            >
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                {s.label}
              </div>
              <div className="text-2xl font-bold text-slate-900 mt-1"
                   data-testid={`insight-count-${s.key}`}>{s.count}</div>
              <div className="text-[11px] text-slate-500 mt-1 line-clamp-2">{s.description}</div>
            </button>
          ))}
        </div>
      )}

      {thresholds && (
        <div className="text-[10px] text-slate-400 pt-2 border-t">
          Thresholds — Big spender ≥ ₹{Number(thresholds.big_spender_min).toLocaleString('en-IN')} ·
          {' '}Dormant ≥ {thresholds.dormant_days}d · Near-expiry ≤ {thresholds.near_expiry_days}d ·
          {' '}Exhausted ≤ {thresholds.wallet_exhaustion_pct}% · High redemption ≥ {thresholds.high_redemption_min}/
          {thresholds.high_redemption_days}d · Recent joiner ≤ {thresholds.recent_joiner_days}d
        </div>
      )}

      {/* Segment drill dialog */}
      <Dialog open={!!active} onOpenChange={(o) => { if (!o) setOpenSegment(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto"
                       data-testid="insight-drill-dialog">
          <DialogHeader>
            <DialogTitle>{active?.label}</DialogTitle>
            <DialogDescription>{active?.description}</DialogDescription>
          </DialogHeader>
          {(active?.members || []).length === 0 ? (
            <div className="text-sm text-gray-500 py-4 text-center">No members in this segment.</div>
          ) : (
            <div className="border rounded overflow-hidden">
              <table className="w-full text-xs" data-testid="insight-drill-table">
                <thead className="bg-slate-50 text-slate-500 border-b">
                  <tr>
                    <th className="text-left p-2">Member</th>
                    <th className="text-left p-2">Phone</th>
                    <th className="text-right p-2">Spend</th>
                    <th className="text-right p-2">Available</th>
                    <th className="text-left p-2">Last Visit</th>
                    <th className="text-left p-2">Next Expiry</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {(active?.members || []).map((m) => (
                    <tr key={m.id} className="border-b last:border-b-0 hover:bg-slate-50">
                      <td className="p-2">
                        <div className="font-medium">{m.name}</div>
                        <div className="text-[10px] text-slate-500">{m.member_id_public}</div>
                      </td>
                      <td className="p-2 text-slate-600">{m.phone || '—'}</td>
                      <td className="p-2 text-right">{INR(m.spend)}</td>
                      <td className="p-2 text-right">{m.vouchers_unused}</td>
                      <td className="p-2 text-slate-500">
                        {m.last_visit_at ? String(m.last_visit_at).slice(0, 10) : 'Never'}
                      </td>
                      <td className="p-2 text-slate-500">
                        {m.next_expiry ? String(m.next_expiry).slice(0, 10) : '—'}
                      </td>
                      <td className="p-2 text-right">
                        <Button size="sm" variant="outline"
                                onClick={() => setOpenMemberId(m.id)}
                                data-testid={`insight-open-${m.id}`}
                                className="h-6 px-2 text-[10px]">
                          Open
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenSegment(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {openMemberId && (
        <MemberDetailDialog memberId={openMemberId}
                            outletId={outletId} outlets={outlets}
                            onClose={() => setOpenMemberId(null)}
                            onChanged={load} />
      )}
    </div>
  );
}

// ============================================================
//                        Members tab
// ============================================================

function MembersTab({ outletId, outlets }) {
  const { getHeaders, API } = useAuth();
  const { canEdit } = useLoyaltyCaps();
  const [q, setQ] = useState('');
  const [missingEmail, setMissingEmail] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [voucherSaleOpen, setVoucherSaleOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (q) params.append('q', q);
      if (missingEmail) params.append('missing_email', 'true');
      if (includeArchived) params.append('include_archived', 'true');
      const res = await axios.get(`${API}/admin/loyalty/members?${params.toString()}`,
                                  { headers: getHeaders() });
      setRows(res.data);
    } catch { toast.error('Failed to load members'); }
  }, [API, getHeaders, q, missingEmail, includeArchived]);

  useEffect(() => { load(); }, [load]);

  // P0 · Cross-device refresh — a mobile Sales user may create a member
  // on another device while this desktop tab is open. Refetch whenever
  // the window regains focus / becomes visible again so the list is
  // never stale for more than one focus event.
  useEffect(() => {
    const onFocus = () => { load(); };
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  const archive = async (m, evt) => {
    evt.stopPropagation();
    if (!window.confirm(`Archive ${m.name}? They will be hidden from the default list but their wallet stays intact.`)) return;
    try {
      await axios.patch(`${API}/admin/loyalty/members/${m.id}/archive`, {}, { headers: getHeaders() });
      toast.success('Member archived');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Archive failed');
    }
  };

  const unarchive = async (m, evt) => {
    evt.stopPropagation();
    try {
      await axios.patch(`${API}/admin/loyalty/members/${m.id}/unarchive`, {}, { headers: getHeaders() });
      toast.success('Member restored');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Restore failed');
    }
  };

  const hardDelete = async (m, evt) => {
    evt.stopPropagation();
    if (!window.confirm(
      `PERMANENTLY delete ${m.name}?\n\nThis is blocked if the member has any vouchers on record.\nRecommended: archive instead.`
    )) return;
    try {
      await axios.delete(`${API}/admin/loyalty/members/${m.id}`, { headers: getHeaders() });
      toast.success('Member deleted');
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = (typeof d === 'object' ? d?.message : d) || 'Delete failed';
      toast.error(msg);
    }
  };

  return (
    <div className="space-y-4 mt-4" data-testid="members-tab">
      <div className="flex justify-between gap-3 items-center flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2 top-2.5 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search by name, phone, member ID, card number or voucher code"
            className="pl-8"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="member-search"
          />
        </div>
        <button
          onClick={() => setMissingEmail((v) => !v)}
          className={`text-xs px-2 py-1 rounded-full border ${missingEmail ? 'bg-amber-100 border-amber-400 text-amber-800' : 'border-gray-300 text-gray-600'}`}
          data-testid="filter-missing-email"
        >
          {missingEmail ? '✓ ' : ''}Missing email
        </button>
        <button
          onClick={() => setIncludeArchived((v) => !v)}
          className={`text-xs px-2 py-1 rounded-full border ${includeArchived ? 'bg-slate-800 text-white border-slate-800' : 'border-gray-300 text-gray-600'}`}
          data-testid="filter-include-archived"
        >
          {includeArchived ? '✓ ' : ''}Show archived
        </button>
        <Button variant="outline" onClick={() => setVoucherSaleOpen(true)}
                data-testid="voucher-sale-btn">
          <Gift className="w-4 h-4 mr-1" />Sell Voucher
        </Button>
        <Button onClick={() => setCreating(true)} data-testid="member-new-btn">
          <Plus className="w-4 h-4 mr-1" />New Member
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500 border-b">
              <tr>
                <th className="text-left p-3">Member ID</th>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Phone</th>
                <th className="text-left p-3">Email</th>
                <th className="text-right p-3">Since</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={6} className="p-6 text-center text-gray-400">No members found.</td></tr>
              )}
              {rows.map((m) => {
                const archived = m.status === 'archived';
                return (
                  <tr key={m.id}
                      className={`border-b last:border-b-0 hover:bg-gray-50 cursor-pointer ${archived ? 'opacity-60' : ''}`}
                      onClick={() => setSelected(m.id)}
                      data-testid={`member-row-${m.id}`}>
                    <td className="p-3 font-mono">
                      {m.member_id_public}
                      {archived && (
                        <span className="ml-2 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-slate-200 text-slate-700"
                              data-testid={`archived-tag-${m.id}`}>
                          Archived
                        </span>
                      )}
                    </td>
                    <td className="p-3">{m.name}</td>
                    <td className="p-3">{m.phone}</td>
                    <td className="p-3 text-gray-600">
                      {m.email || (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                          Missing email
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-right text-xs text-gray-500">
                      {m.created_at ? String(m.created_at).slice(0, 10) : '—'}
                    </td>
                    <td className="p-3 text-right space-x-1">
                      {canEdit ? (archived ? (
                        <Button size="sm" variant="outline"
                                onClick={(e) => unarchive(m, e)}
                                data-testid={`unarchive-btn-${m.id}`}>
                          Restore
                        </Button>
                      ) : (
                        <Button size="sm" variant="ghost"
                                onClick={(e) => archive(m, e)}
                                data-testid={`archive-btn-${m.id}`}
                                title="Archive member">
                          <Archive className="w-3.5 h-3.5" />
                        </Button>
                      )) : null}
                      {canEdit && (
                      <Button size="sm" variant="ghost"
                              onClick={(e) => hardDelete(m, e)}
                              data-testid={`delete-btn-${m.id}`}
                              title="Delete permanently (blocked if any vouchers exist)">
                        <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                      </Button>
                      )}
                      <Button size="sm" variant="ghost">Open</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {creating && (
        <MemberCreator onClose={() => setCreating(false)}
                       onCreated={(m) => { setCreating(false); setSelected(m.id); load(); }} />
      )}
      {selected && (
        <MemberDetailDialog memberId={selected} outletId={outletId} outlets={outlets}
                            onClose={() => setSelected(null)} onChanged={load} />
      )}
      {voucherSaleOpen && (
        <VoucherSaleDialog outletId={outletId}
                            onClose={() => setVoucherSaleOpen(false)}
                            onSold={() => { setVoucherSaleOpen(false); load(); }} />
      )}
    </div>
  );
}

function MemberCreator({ onClose, onCreated }) {
  const { getHeaders, API } = useAuth();
  // ITER-B1 — Birthday is day-month only (MM-DD). We collect month + day
  // separately so the picker never asks the guest for a year.
  const [form, setForm] = useState({
    name: '', phone: '', email: '',
    birthdayMonth: '', birthdayDay: '',
    anniversary: '', notes: '',
  });
  const [saving, setSaving] = useState(false);

  const emailIsValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  const birthdayStr = (form.birthdayMonth && form.birthdayDay)
    ? `${form.birthdayMonth}-${form.birthdayDay}` : '';

  const save = async () => {
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim()) {
      toast.error('Name, phone and email are all required');
      return;
    }
    if (!emailIsValid) {
      toast.error('Please enter a valid email address');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name, phone: form.phone, email: form.email,
        birthday: birthdayStr, anniversary: form.anniversary, notes: form.notes,
      };
      const res = await axios.post(`${API}/admin/loyalty/members`, payload, { headers: getHeaders() });
      toast.success(`Member created: ${res.data.member_id_public}`);
      onCreated(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Create failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="member-creator">
        <DialogHeader><DialogTitle>New Member</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Name *">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="member-name" />
          </Field>
          <Field label="Phone (10 digits) *">
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="member-phone" />
          </Field>
          <Field label="Email * (used for login OTP)">
            <Input type="email" value={form.email}
                   onChange={(e) => setForm({ ...form, email: e.target.value })}
                   data-testid="member-email"
                   placeholder="member@example.com" />
            {form.email && !emailIsValid && (
              <div className="text-xs text-rose-600 mt-1">Please enter a valid email address</div>
            )}
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Birthday (day / month)">
              <BirthdayPicker
                month={form.birthdayMonth} day={form.birthdayDay}
                onChange={(m, d) => setForm({ ...form, birthdayMonth: m, birthdayDay: d })}
                testIdPrefix="member-birthday"
              />
            </Field>
            <Field label="Anniversary"><Input type="date" value={form.anniversary} onChange={(e) => setForm({ ...form, anniversary: e.target.value })} /></Field>
          </div>
          <Field label="Notes"><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || !emailIsValid || !form.name || !form.phone}
                  data-testid="member-save">
            {saving ? 'Saving…' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ITER-B1 — Birthday picker: month + day, no year. Purely presentational. */
const MONTHS = [
  ['01','Jan'],['02','Feb'],['03','Mar'],['04','Apr'],['05','May'],['06','Jun'],
  ['07','Jul'],['08','Aug'],['09','Sep'],['10','Oct'],['11','Nov'],['12','Dec'],
];
const DAYS_IN_MONTH = { '01':31,'02':29,'03':31,'04':30,'05':31,'06':30,
                        '07':31,'08':31,'09':30,'10':31,'11':30,'12':31 };

function BirthdayPicker({ month, day, onChange, testIdPrefix = 'birthday' }) {
  const daysMax = DAYS_IN_MONTH[month] || 31;
  const days = Array.from({ length: daysMax }, (_, i) =>
    String(i + 1).padStart(2, '0'));
  return (
    <div className="flex gap-2">
      <select className="h-9 border rounded px-2 text-sm flex-1"
              value={month}
              onChange={(e) => onChange(e.target.value, day)}
              data-testid={`${testIdPrefix}-month`}>
        <option value="">Month</option>
        {MONTHS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      <select className="h-9 border rounded px-2 text-sm flex-1"
              value={day}
              onChange={(e) => onChange(month, e.target.value)}
              data-testid={`${testIdPrefix}-day`}
              disabled={!month}>
        <option value="">Day</option>
        {days.map((d) => <option key={d} value={d}>{d}</option>)}
      </select>
    </div>
  );
}

/* ITER-B8 — Individual Voucher Sale dialog. Sells one or more voucher
   types to an existing member (searched by phone/email) or creates a
   walk-in member on the fly. Uses POST /admin/loyalty/voucher-sale. */
function VoucherSaleDialog({ outletId, onClose, onSold, presetMember = null }) {
  const { getHeaders, API } = useAuth();
  // ITER-loyalty-p1 · presetMember: when opened from Member Detail we
  // arrive with a resolved member — mode locked to 'existing' from the
  // start so the sale posts member_id, not walk_in.
  const [mode, setMode] = useState(presetMember ? 'existing' : 'walkin');   // 'existing' | 'walkin'
  const [phone, setPhone] = useState(presetMember?.phone || '');
  const [email, setEmail] = useState(presetMember?.email || '');
  const [name, setName] = useState(presetMember?.name || '');
  const [existing, setExisting] = useState(presetMember || null);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [voucherTypes, setVoucherTypes] = useState([]);
  const [lines, setLines] = useState([{ voucher_type_id: '', count: 1 }]);
  const [price, setPrice] = useState('');
  const [payMethod, setPayMethod] = useState('cash');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const phoneValid = /^\d{10}$/.test(phone.replace(/\D/g, ''));

  useEffect(() => {
    if (!outletId) return;
    axios.get(`${API}/admin/loyalty/voucher-types?outlet_id=${outletId}`,
              { headers: getHeaders() })
      .then((r) => setVoucherTypes(r.data.filter((v) => v.active !== false)))
      .catch(() => toast.error('Failed to load voucher types'));
  }, [API, getHeaders, outletId]);

  const lookupExisting = async () => {
    if (!phoneValid && !emailValid) {
      toast.error('Enter a valid phone or email to look up');
      return;
    }
    setLookupBusy(true);
    try {
      const q = phoneValid ? phone : email;
      const r = await axios.get(
        `${API}/admin/loyalty/members?q=${encodeURIComponent(q)}&limit=5`,
        { headers: getHeaders() },
      );
      const active = (r.data || []).filter((m) => m.status !== 'archived');
      if (active.length === 0) {
        toast.info('No matching member found — a walk-in will be created on save.');
        setExisting(null);
        setMode('walkin');
      } else {
        // ITER-loyalty-p1 · Explicit mode-switch on successful lookup.
        // Previously the dialog left mode='walkin', and the backend would
        // then try to walk-in-dedupe by phone, occasionally creating a
        // second member. We now lock into 'existing' so the sale posts
        // the resolved member_id.
        setExisting(active[0]);
        setMode('existing');
        setName(active[0].name);
        toast.success(`Found: ${active[0].name} (${active[0].member_id_public})`);
      }
    } catch { toast.error('Lookup failed'); }
    finally { setLookupBusy(false); }
  };

  const updateLine = (idx, patch) =>
    setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const addLine = () =>
    setLines((ls) => [...ls, { voucher_type_id: '', count: 1 }]);
  const removeLine = (idx) =>
    setLines((ls) => ls.filter((_, i) => i !== idx));

  const submit = async () => {
    if (lines.length === 0 || lines.some((l) => !l.voucher_type_id)) {
      toast.error('Pick a voucher type for every line');
      return;
    }
    if (mode === 'existing' && existing?.id) {
      // ITER-loyalty-p1 · Existing-member path skips walk-in validation.
      if (!price || Number(price) < 0) {
        toast.error('Enter the amount collected');
        return;
      }
    } else {
      if (!phoneValid || !emailValid || !name.trim()) {
        toast.error('Name, valid phone and valid email are required');
        return;
      }
      if (!price || Number(price) < 0) {
        toast.error('Enter the amount collected');
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        outlet_id: outletId,
        lines: lines.map((l) => ({
          voucher_type_id: l.voucher_type_id,
          count: Number(l.count) || 1,
        })),
        price_paid: Number(price),
        payment_method: payMethod,
        notes: notes.trim(),
      };
      if (mode === 'existing' && existing) {
        payload.member_id = existing.id;
      } else {
        payload.walk_in = { name: name.trim(), phone, email };
      }
      const r = await axios.post(
        `${API}/admin/loyalty/voucher-sale`, payload, { headers: getHeaders() });
      toast.success(`Sold ${r.data.vouchers_issued} voucher(s) · Card ${r.data.card.card_number}`);
      onSold();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === 'object' ? d?.message : d) || 'Sale failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="voucher-sale-dialog">
        <DialogHeader>
          <DialogTitle>Sell Voucher(s)</DialogTitle>
          <DialogDescription>
            Sell individual vouchers without a full membership. Look up an
            existing member by phone or email — if none found, a walk-in
            profile is created from the details below.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone *">
              <Input value={phone} onChange={(e) => setPhone(e.target.value)}
                     placeholder="10-digit"
                     data-testid="voucher-sale-phone" />
            </Field>
            <Field label="Email *">
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                     placeholder="guest@example.com"
                     data-testid="voucher-sale-email" />
            </Field>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={lookupExisting}
                    disabled={lookupBusy}
                    data-testid="voucher-sale-lookup">
              {lookupBusy ? 'Looking up…' : 'Look up existing member'}
            </Button>
            {existing && (
              <div className="text-xs text-emerald-700"
                   data-testid="voucher-sale-existing">
                ✓ {existing.name} · {existing.member_id_public}
                <button className="ml-2 underline text-slate-500"
                        onClick={() => { setExisting(null); setMode('walkin'); }}>
                  clear
                </button>
              </div>
            )}
          </div>
          <Field label="Name *">
            <Input value={name} onChange={(e) => setName(e.target.value)}
                   disabled={!!existing}
                   data-testid="voucher-sale-name" />
          </Field>

          <div className="border-t pt-3 space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Vouchers to Sell</Label>
              <Button size="sm" variant="outline" onClick={addLine}
                      data-testid="voucher-sale-add-line">
                <Plus className="w-3 h-3 mr-1" />Add line
              </Button>
            </div>
            {lines.map((l, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-8">
                  <Select value={l.voucher_type_id || ''}
                          onValueChange={(v) => updateLine(idx, { voucher_type_id: v })}>
                    <SelectTrigger className="h-9" data-testid={`voucher-sale-vt-${idx}`}>
                      <SelectValue placeholder="Voucher type" />
                    </SelectTrigger>
                    <SelectContent>
                      {voucherTypes.length === 0 && (
                        <div className="p-2 text-xs text-gray-400">No active voucher types.</div>
                      )}
                      {voucherTypes.map((v) => (
                        <SelectItem key={v.id} value={v.id}>{v.name} ({v.code})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-3">
                  <Input type="number" min={1} max={100}
                         value={l.count}
                         onChange={(e) => updateLine(idx, { count: e.target.value })}
                         data-testid={`voucher-sale-count-${idx}`} />
                </div>
                <div className="col-span-1 text-right">
                  <Button size="sm" variant="ghost"
                          disabled={lines.length === 1}
                          onClick={() => removeLine(idx)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3 border-t pt-3">
            <Field label="Amount collected (₹) *">
              <Input type="number" min="0" value={price}
                     onChange={(e) => setPrice(e.target.value)}
                     data-testid="voucher-sale-price" />
            </Field>
            <Field label="Payment method">
              <Select value={payMethod} onValueChange={setPayMethod}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Notes">
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving}
                  data-testid="voucher-sale-submit">
            {saving ? 'Saving…' : `Sell${existing ? ` to ${existing.name}` : ' (walk-in)'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



function MemberDetailDialog({ memberId, outletId, outlets, onClose, onChanged }) {
  const { getHeaders, API } = useAuth();
  const { canEdit } = useLoyaltyCaps();
  const [detail, setDetail] = useState(null);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [voucherSaleOpen, setVoucherSaleOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [tab, setTab] = useState('overview');
  const [voucherFilter, setVoucherFilter] = useState('available');
  // Loyalty Pass 2 · Hold / Resume / Replace lifecycle dialog state.
  const [lifecycleAction, setLifecycleAction] = useState(null); // { card, action }
  const [lifecycleReason, setLifecycleReason] = useState('');
  const [lifecycleBusy, setLifecycleBusy] = useState(false);

  // Final closure · Member status control (Hold / Resume / Blacklist / Restore).
  const [manageOpen, setManageOpen] = useState(false);
  const [manageAction, setManageAction] = useState(null);       // 'hold'|'resume'|'blacklist'|'restore'
  const [manageReason, setManageReason] = useState('');
  const [manageNote, setManageNote] = useState('');
  const [manageBusy, setManageBusy] = useState(false);

  // Final closure · Voucher-level multi-select + Pause/Resume/Extend.
  const [selectedVoucherIds, setSelectedVoucherIds] = useState(new Set());
  const [voucherAction, setVoucherAction] = useState(null);     // 'pause'|'resume'|'extend'
  const [voucherActionBusy, setVoucherActionBusy] = useState(false);
  const [voucherReason, setVoucherReason] = useState('');
  const [extendDaysPreset, setExtendDaysPreset] = useState(30);
  const [extendMode, setExtendMode] = useState('days');        // 'days' | 'date'
  const [extendCustomDate, setExtendCustomDate] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/loyalty/members/${memberId}`, { headers: getHeaders() });
      setDetail(res.data);
    } catch { toast.error('Failed to load member'); }
  }, [API, getHeaders, memberId]);

  useEffect(() => { load(); }, [load]);

  if (!detail) return null;

  // Filter out `replaced` cards from primary view (kept in audit log via activity).
  const visibleCards = (detail.cards || []).filter((c) => c.status !== 'replaced');
  const hasActiveCard = visibleCards.some(c => c.status === 'active');
  const kpis = detail.kpis || {};
  const activity = detail.activity_events || [];
  const vouchers = detail.vouchers || [];

  const openLifecycle = (card, action) => {
    setLifecycleAction({ card, action });
    setLifecycleReason('');
  };
  const closeLifecycle = () => { setLifecycleAction(null); setLifecycleReason(''); };
  const submitLifecycle = async () => {
    if (!lifecycleAction) return;
    const { card, action } = lifecycleAction;
    setLifecycleBusy(true);
    try {
      await axios.post(
        `${API}/admin/loyalty/members/${memberId}/cards/${card.id}/${action}`,
        { reason: lifecycleReason.trim() || null },
        { headers: getHeaders() },
      );
      toast.success(
        action === 'hold'    ? 'Card put on hold'    :
        action === 'resume'  ? 'Card resumed'        :
                               'Card replaced — new card issued'
      );
      closeLifecycle();
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally { setLifecycleBusy(false); }
  };

  const filteredVouchers = vouchers.filter((v) => {
    if (voucherFilter === 'all') return true;
    if (voucherFilter === 'available') return v.status === 'unused';
    if (voucherFilter === 'redeemed')  return v.status === 'redeemed';
    if (voucherFilter === 'expired')   return ['expired', 'voided', 'cancelled'].includes(v.status);
    return true;
  });
  const outletName = (id) => outlets?.find((o) => o.id === id)?.name || (id ? id.slice(0, 8) : '—');

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="member-detail">
        {/* HEADER */}
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 flex-wrap">
            <span className="text-lg">{detail.name}</span>
            <Badge variant="outline" data-testid="member-detail-public-id">{detail.member_id_public}</Badge>
            <Badge className={
              detail.status === 'blacklisted' ? 'bg-rose-50 text-rose-800 border-rose-200' :
              detail.status === 'on_hold'     ? 'bg-amber-50 text-amber-800 border-amber-200' :
              detail.status === 'active'      ? 'bg-emerald-50 text-emerald-800 border-emerald-200' :
                                                'bg-slate-100 text-slate-700 border-slate-200'}
                   data-testid="member-detail-status">
              {(detail.status || 'active').replace('_', ' ').toUpperCase()}
            </Badge>
            {kpis.wallet_exhausted && (
              <Badge className="bg-amber-50 text-amber-800 border-amber-200" data-testid="wallet-exhausted-chip">
                Voucher Wallet Exhausted
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {detail.phone || '—'} · {detail.email || 'no email'} · Since {String(detail.created_at || '').slice(0, 10)}
            {detail.birthday ? ` · Birthday ${detail.birthday}` : ''}
            {detail.anniversary ? ` · Anniversary ${String(detail.anniversary).slice(0, 10)}` : ''}
          </DialogDescription>
        </DialogHeader>

        {/* ACTIONS */}
        <div className="flex justify-end gap-2 flex-wrap py-1">
          {canEdit && (
          <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}
                  data-testid="member-edit-btn">
            <Pencil className="w-3.5 h-3.5 mr-1" /> Edit Details
          </Button>
          )}
          {canEdit && (
          <Button size="sm" variant="outline" onClick={() => setManageOpen(true)}
                  data-testid="member-manage-btn">
            Manage Membership
          </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => setVoucherSaleOpen(true)}
                  data-testid="member-sell-voucher-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add / Sell Voucher
          </Button>
          {canEdit && (
          <Button size="sm" variant="outline"
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/admin/loyalty/members/${memberId}/resend-welcome`,
                                        { kind: 'welcome' }, { headers: getHeaders() });
                      toast.success('Welcome email queued');
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'Send failed');
                    }
                  }}
                  disabled={!hasActiveCard} data-testid="resend-welcome-btn">
            Resend Welcome Email
          </Button>
          )}
          <Button size="sm" onClick={() => setPurchaseOpen(true)} data-testid="member-purchase-btn">
            <CreditCard className="w-4 h-4 mr-1" />
            {hasActiveCard ? 'Renew / Buy Another Kit' : 'Sell Kit'}
          </Button>
        </div>

        {/* KPI ROW */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 py-2">
          <StatChip label="Revenue"      value={INR(kpis.member_revenue || 0)} data-testid="kpi-revenue" />
          <StatChip label="Last Visit"   value={kpis.last_visit_at ? String(kpis.last_visit_at).slice(0, 10) : 'Never'} />
          <StatChip label="Available"    value={kpis.available_count ?? 0} data-testid="kpi-available" />
          <StatChip label="Redeemed"     value={kpis.redeemed_count ?? 0} data-testid="kpi-redeemed" />
          <StatChip label="Expired"      value={kpis.expired_count ?? 0} data-testid="kpi-expired" />
          <StatChip label="Membership Spend" value={INR(kpis.membership_spend || 0)} data-testid="kpi-spend" />
        </div>

        {/* REMARKS / GUEST NOTES */}
        <div className="border border-amber-200 bg-amber-50/50 rounded-md p-3" data-testid="member-remarks">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-amber-800 mb-1">
            Remarks / Guest Notes
          </div>
          <div className="text-sm text-slate-800 whitespace-pre-wrap">
            {(detail.notes && detail.notes.trim()) || <span className="text-slate-500 italic">No remarks recorded</span>}
          </div>
        </div>

        {/* TABS */}
        <Tabs value={tab} onValueChange={setTab} className="pt-2">
          <TabsList>
            <TabsTrigger value="overview" data-testid="mem-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="vouchers" data-testid="mem-tab-vouchers">Vouchers</TabsTrigger>
            <TabsTrigger value="activity" data-testid="mem-tab-activity">Activity</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="pt-3">
            <h4 className="text-sm font-semibold mb-2">Membership Cards</h4>
            {visibleCards.length === 0 && <div className="text-sm text-gray-500">No cards yet.</div>}
            {visibleCards.map((c) => (
              <div key={c.id} className="border rounded p-3 text-sm mb-2 bg-gradient-to-r from-slate-50 to-white"
                   data-testid={`member-card-${c.id}`}>
                <div className="flex justify-between items-start gap-3">
                  <div className="min-w-0">
                    <div className="font-mono text-xs text-gray-500">{c.card_number}</div>
                    <div className="font-medium">{c.program_name || c.kit_name || 'Membership'}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Paid {INR(c.price_paid)} · Issued {String(c.issued_at || '').slice(0, 10)}
                      {c.expires_at ? ` → Expires ${String(c.expires_at).slice(0, 10)}` : ''}
                    </div>
                    {c.last_top_up_at && (
                      <div className="text-xs text-gray-500">Last top-up {String(c.last_top_up_at).slice(0, 10)}</div>
                    )}
                    {c.status === 'held' && (
                      <div className="text-xs text-amber-700 mt-1" data-testid={`card-hold-reason-${c.id}`}>
                        <span className="font-semibold">On hold</span>
                        {c.hold_reason ? <> — {c.hold_reason}</> : null}
                        {c.held_at ? <span className="text-slate-500"> · {String(c.held_at).slice(0, 10)}</span> : null}
                      </div>
                    )}
                    {c.replaced_from_number && (
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        Replaces card <span className="font-mono">{c.replaced_from_number}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    <Badge
                      variant={c.status === 'active' ? 'default' : 'outline'}
                      className={
                        c.status === 'held'
                          ? 'bg-amber-100 text-amber-800 border-amber-200'
                          : ''
                      }
                    >
                      {c.status}
                    </Badge>
                    <div className="flex gap-1.5 flex-wrap justify-end">
                      {canEdit && c.status === 'active' && (
                        <Button size="sm" variant="outline"
                                onClick={() => openLifecycle(c, 'hold')}
                                data-testid={`card-hold-${c.id}`}
                                className="h-7 px-2 text-[11px]">
                          Hold
                        </Button>
                      )}
                      {canEdit && c.status === 'held' && (
                        <Button size="sm" variant="outline"
                                onClick={() => openLifecycle(c, 'resume')}
                                data-testid={`card-resume-${c.id}`}
                                className="h-7 px-2 text-[11px]">
                          Resume
                        </Button>
                      )}
                      {canEdit && (c.status === 'active' || c.status === 'held') && (
                        <Button size="sm" variant="outline"
                                onClick={() => openLifecycle(c, 'replace')}
                                data-testid={`card-replace-${c.id}`}
                                className="h-7 px-2 text-[11px]">
                          Replace
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="vouchers" className="pt-3">
            <div className="flex gap-1 mb-2 border-b">
              {[
                ['available', `Available (${kpis.available_count ?? 0})`],
                ['redeemed',  `Redeemed (${kpis.redeemed_count ?? 0})`],
                ['expired',   `Expired / Unavailable (${kpis.expired_count ?? 0})`],
                ['all',       `All (${kpis.total_vouchers ?? vouchers.length})`],
              ].map(([k, label]) => (
                <button key={k}
                        onClick={() => { setVoucherFilter(k); setSelectedVoucherIds(new Set()); }}
                        data-testid={`vfilter-${k}`}
                        className={`text-xs px-3 py-1.5 border-b-2 ${voucherFilter === k
                          ? 'border-[color:var(--color-primary)] text-[color:var(--color-primary)] font-semibold'
                          : 'border-transparent text-slate-600 hover:text-slate-900'}`}>
                  {label}
                </button>
              ))}
            </div>

            {canEdit && selectedVoucherIds.size > 0 && (
              <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded p-2 mb-2 text-xs"
                   data-testid="voucher-multi-action-bar">
                <div>
                  <strong>{selectedVoucherIds.size}</strong> voucher{selectedVoucherIds.size === 1 ? '' : 's'} selected
                </div>
                <div className="flex gap-2">
                  {filteredVouchers.some(v => selectedVoucherIds.has(v.id) && v.status === 'unused') && (
                    <Button size="sm" variant="outline" className="h-7 text-[11px]"
                            onClick={() => setVoucherAction('pause')}
                            data-testid="voucher-pause-btn">
                      Pause Selected
                    </Button>
                  )}
                  {filteredVouchers.some(v => selectedVoucherIds.has(v.id) && v.status === 'paused') && (
                    <Button size="sm" variant="outline" className="h-7 text-[11px]"
                            onClick={() => setVoucherAction('resume')}
                            data-testid="voucher-resume-btn">
                      Resume Selected
                    </Button>
                  )}
                  {filteredVouchers.some(v => selectedVoucherIds.has(v.id) && ['unused','paused','expired'].includes(v.status)) && (
                    <Button size="sm" className="h-7 text-[11px] bg-amber-500 hover:bg-amber-600 text-white"
                            onClick={() => setVoucherAction('extend')}
                            data-testid="voucher-extend-btn">
                      Extend Validity
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="h-7 text-[11px]"
                          onClick={() => setSelectedVoucherIds(new Set())}>
                    Clear
                  </Button>
                </div>
              </div>
            )}
            {filteredVouchers.length === 0 ? (
              <div className="text-sm text-gray-500 py-6 text-center">No vouchers in this bucket.</div>
            ) : (
              <div className="max-h-72 overflow-y-auto border rounded">
                <table className="w-full text-xs" data-testid="member-vouchers-table">
                  <thead className="text-gray-500 border-b bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2 w-6">
                        <input type="checkbox"
                               checked={filteredVouchers.length > 0 && filteredVouchers.every(v => selectedVoucherIds.has(v.id))}
                               onChange={(e) => {
                                 const ns = new Set(selectedVoucherIds);
                                 if (e.target.checked) filteredVouchers.forEach(v => ns.add(v.id));
                                 else filteredVouchers.forEach(v => ns.delete(v.id));
                                 setSelectedVoucherIds(ns);
                               }}
                               data-testid="voucher-select-all" />
                      </th>
                      <th className="text-left p-2">Code</th>
                      <th className="text-left p-2">Type</th>
                      <th className="text-left p-2">Status</th>
                      {voucherFilter === 'redeemed' ? (
                        <>
                          <th className="text-left p-2">Redeemed</th>
                          <th className="text-left p-2">Outlet</th>
                          <th className="text-left p-2">Bill Ref</th>
                          <th className="text-right p-2">Bill</th>
                          <th className="text-right p-2">Discount</th>
                        </>
                      ) : (
                        <>
                          <th className="text-left p-2">Issued</th>
                          <th className="text-right p-2">Valid Until</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVouchers.map((v) => (
                      <tr key={v.id} className="border-b last:border-b-0">
                        <td className="p-2">
                          <input type="checkbox"
                                 checked={selectedVoucherIds.has(v.id)}
                                 onChange={(e) => {
                                   const ns = new Set(selectedVoucherIds);
                                   if (e.target.checked) ns.add(v.id);
                                   else ns.delete(v.id);
                                   setSelectedVoucherIds(ns);
                                 }}
                                 data-testid={`voucher-select-${v.id}`} />
                        </td>
                        <td className="p-2 font-mono">{v.voucher_code}</td>
                        <td className="p-2">{v.voucher_type_name}</td>
                        <td className="p-2">
                          <Badge variant="outline" className="text-xs">{v.status}</Badge>
                          {v.is_custom_addon && <span className="ml-1 text-xs text-amber-600">addon</span>}
                        </td>
                        {voucherFilter === 'redeemed' ? (
                          <>
                            <td className="p-2 text-gray-500">{String(v.redemption?.redeemed_at || v.redeemed_at || '').slice(0, 16).replace('T', ' ')}</td>
                            <td className="p-2 text-gray-500">{outletName(v.redemption?.redemption_outlet)}</td>
                            <td className="p-2 font-mono text-[11px]">{v.redemption?.bill_reference || '—'}</td>
                            <td className="p-2 text-right">{INR(v.redemption?.bill_amount || 0)}</td>
                            <td className="p-2 text-right text-emerald-700">{INR(v.redemption?.discount_amount || 0)}</td>
                          </>
                        ) : (
                          <>
                            <td className="p-2 text-gray-500">{String(v.issued_at || '').slice(0, 10)}</td>
                            <td className="p-2 text-right text-gray-500">{String(v.valid_until || '').slice(0, 10)}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="activity" className="pt-3">
            {activity.length === 0 ? (
              <div className="text-sm text-gray-500 py-6 text-center">No activity yet.</div>
            ) : (
              <div className="space-y-2" data-testid="member-activity-list">
                {activity.map((ev, i) => (
                  <div key={i} className="border rounded p-2.5 text-xs bg-white"
                       data-testid={`activity-${ev.type}`}>
                    <div className="flex justify-between items-start gap-2">
                      <span className="font-semibold text-slate-800">{ev.label}</span>
                      <span className="text-slate-500 tabular-nums">{String(ev.at || '').slice(0, 16).replace('T', ' ')}</span>
                    </div>
                    {ev.meta && (
                      <div className="text-slate-600 mt-1">
                        {ev.meta.card_number && <span>Card {ev.meta.card_number} · </span>}
                        {ev.meta.program && <span>{ev.meta.program} · </span>}
                        {ev.meta.voucher_type && <span>{ev.meta.voucher_type} · </span>}
                        {ev.meta.code && <span className="font-mono">{ev.meta.code}</span>}
                        {ev.meta.voucher_code && <span className="font-mono">{ev.meta.voucher_code} · </span>}
                        {ev.meta.outlet_id && <span>at {outletName(ev.meta.outlet_id)} · </span>}
                        {ev.meta.bill_amount != null && <span>Bill {INR(ev.meta.bill_amount)}</span>}
                        {ev.meta.discount != null && ev.meta.discount > 0 && <span> · Discount {INR(ev.meta.discount)}</span>}
                        {ev.meta.bill_reference && <span> · Ref {ev.meta.bill_reference}</span>}
                        {ev.meta.amount != null && ev.meta.amount > 0 && !ev.meta.bill_amount && <span>Amount {INR(ev.meta.amount)}</span>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {purchaseOpen && (
          <KitPurchaseDialog member={detail} outletId={outletId} outlets={outlets}
                             onClose={() => setPurchaseOpen(false)}
                             onDone={() => { setPurchaseOpen(false); load(); onChanged?.(); }} />
        )}
        {voucherSaleOpen && (
          <VoucherSaleDialog outletId={outletId}
                             presetMember={{ id: detail.id, name: detail.name,
                                             phone: detail.phone, email: detail.email }}
                             onClose={() => setVoucherSaleOpen(false)}
                             onSold={() => { setVoucherSaleOpen(false); load(); onChanged?.(); }} />
        )}
        {editOpen && (
          <MemberEditDialog member={detail}
                            onClose={() => setEditOpen(false)}
                            onSaved={() => { setEditOpen(false); load(); onChanged?.(); }} />
        )}

        {/* Loyalty Pass 2 · Hold / Resume / Replace confirm dialog */}
        <Dialog open={!!lifecycleAction} onOpenChange={(o) => { if (!o) closeLifecycle(); }}>
          <DialogContent className="max-w-md" data-testid="card-lifecycle-dialog">
            <DialogHeader>
              <DialogTitle>
                {lifecycleAction?.action === 'hold'    && 'Put card on hold'}
                {lifecycleAction?.action === 'resume'  && 'Resume card'}
                {lifecycleAction?.action === 'replace' && 'Replace card'}
              </DialogTitle>
              <DialogDescription>
                {lifecycleAction?.action === 'hold' &&
                  "Card will be marked held. Existing vouchers remain but new redemptions can be gated. Reversible."}
                {lifecycleAction?.action === 'resume' &&
                  "Card returns to active. Only allowed when no other active card exists at the same outlet and the expiry has not passed."}
                {lifecycleAction?.action === 'replace' &&
                  "A new card number is issued. All vouchers still on the old card move to the new one. Old card is retired to the audit log."}
              </DialogDescription>
            </DialogHeader>
            <div className="py-2 text-xs text-slate-600">
              Card <span className="font-mono">{lifecycleAction?.card?.card_number}</span>
              {' · '}{lifecycleAction?.card?.program_name || 'Membership'}
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">
                Reason {lifecycleAction?.action === 'replace' ? '(recommended)' : '(optional)'}
              </Label>
              <Textarea rows={2} value={lifecycleReason}
                        onChange={(e) => setLifecycleReason(e.target.value)}
                        placeholder={
                          lifecycleAction?.action === 'hold'    ? 'e.g. guest paused membership' :
                          lifecycleAction?.action === 'resume'  ? 'e.g. guest resumed at counter' :
                                                                  'e.g. card lost / damaged'
                        }
                        data-testid="lifecycle-reason" />
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={closeLifecycle}>Cancel</Button>
              <Button onClick={submitLifecycle} disabled={lifecycleBusy}
                      data-testid="lifecycle-confirm">
                {lifecycleBusy ? 'Working…' :
                 lifecycleAction?.action === 'hold'    ? 'Put on Hold' :
                 lifecycleAction?.action === 'resume'  ? 'Resume Card' :
                                                        'Replace & Issue New'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Final closure · Manage Membership (Hold / Resume / Blacklist / Restore) */}
        <ManageMembershipDialog
          open={manageOpen}
          onOpenChange={(o) => {
            setManageOpen(o);
            if (!o) { setManageAction(null); setManageReason(''); setManageNote(''); }
          }}
          detail={detail}
          action={manageAction}
          setAction={setManageAction}
          reason={manageReason} setReason={setManageReason}
          note={manageNote} setNote={setManageNote}
          busy={manageBusy}
          onSubmit={async () => {
            if (!manageAction) return;
            if (!manageReason.trim()) { toast.error('Reason is required'); return; }
            setManageBusy(true);
            try {
              await axios.post(
                `${API}/admin/loyalty/members/${memberId}/${manageAction}`,
                { reason: manageReason.trim(), internal_note: manageNote.trim() || null },
                { headers: getHeaders() },
              );
              toast.success(
                manageAction === 'hold'      ? 'Membership placed on hold' :
                manageAction === 'resume'    ? 'Membership resumed'        :
                manageAction === 'blacklist' ? 'Membership blacklisted'    :
                                               'Membership restored'
              );
              setManageOpen(false);
              setManageAction(null); setManageReason(''); setManageNote('');
              await load(); onChanged?.();
            } catch (e) {
              toast.error(e?.response?.data?.detail || 'Action failed');
            } finally { setManageBusy(false); }
          }}
        />

        {/* Final closure · Voucher pause / resume / extend (multi-select) */}
        <VoucherActionDialog
          action={voucherAction}
          onClose={() => {
            setVoucherAction(null); setVoucherReason(''); setExtendCustomDate('');
          }}
          selectedIds={Array.from(selectedVoucherIds)}
          vouchers={vouchers}
          busy={voucherActionBusy}
          reason={voucherReason} setReason={setVoucherReason}
          extendMode={extendMode} setExtendMode={setExtendMode}
          extendDaysPreset={extendDaysPreset} setExtendDaysPreset={setExtendDaysPreset}
          extendCustomDate={extendCustomDate} setExtendCustomDate={setExtendCustomDate}
          onSubmit={async () => {
            if (!voucherReason.trim()) { toast.error('Reason is required'); return; }
            setVoucherActionBusy(true);
            try {
              if (voucherAction === 'pause') {
                for (const vid of selectedVoucherIds) {
                  await axios.post(`${API}/admin/loyalty/vouchers/${vid}/pause`,
                    { reason: voucherReason.trim() }, { headers: getHeaders() });
                }
              } else if (voucherAction === 'resume') {
                for (const vid of selectedVoucherIds) {
                  await axios.post(`${API}/admin/loyalty/vouchers/${vid}/resume`,
                    { reason: voucherReason.trim() }, { headers: getHeaders() });
                }
              } else if (voucherAction === 'extend') {
                const payload = {
                  voucher_instance_ids: Array.from(selectedVoucherIds),
                  reason: voucherReason.trim(),
                };
                if (extendMode === 'days') payload.days = extendDaysPreset;
                else payload.new_valid_until = new Date(extendCustomDate).toISOString();
                await axios.post(
                  `${API}/admin/loyalty/members/${memberId}/vouchers/extend-validity`,
                  payload, { headers: getHeaders() },
                );
              }
              toast.success(
                voucherAction === 'pause' ? 'Vouchers paused' :
                voucherAction === 'resume' ? 'Vouchers resumed' :
                                             'Validity extended'
              );
              setVoucherAction(null); setSelectedVoucherIds(new Set());
              setVoucherReason(''); setExtendCustomDate('');
              await load(); onChanged?.();
            } catch (e) {
              const d = e?.response?.data?.detail;
              toast.error(typeof d === 'string' ? d : (d?.message || 'Action failed'));
            } finally { setVoucherActionBusy(false); }
          }}
        />

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MemberEditDialog({ member, onClose, onSaved }) {
  const { getHeaders, API } = useAuth();
  const [form, setForm] = useState({
    name:        member.name || '',
    phone:       member.phone || '',
    email:       member.email || '',
    birthday:    member.birthday || '',
    anniversary: member.anniversary || '',
    notes:       member.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    // Only send changed fields — the PATCH endpoint accepts them all as optional.
    const patch = {};
    for (const k of Object.keys(form)) {
      if ((form[k] || '') !== (member[k] || '')) patch[k] = form[k];
    }
    if (Object.keys(patch).length === 0) { onClose(); return; }
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/loyalty/members/${member.id}`, patch,
                        { headers: getHeaders() });
      toast.success('Member updated');
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="member-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit Member Details</DialogTitle>
        </DialogHeader>
        <div className="space-y-2 py-2">
          {[
            ['name',        'Name'],
            ['phone',       'Phone (10 digits)'],
            ['email',       'Email'],
            ['birthday',    'Birthday (MM-DD or YYYY-MM-DD)'],
            ['anniversary', 'Anniversary (YYYY-MM-DD)'],
          ].map(([k, label]) => (
            <div key={k}>
              <Label className="text-xs">{label}</Label>
              <Input value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                     className="h-9" data-testid={`member-edit-${k}`} />
            </div>
          ))}
          <div>
            <Label className="text-xs">Remarks / Guest Notes</Label>
            <textarea rows={3} value={form.notes}
                      onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                      className="w-full border rounded p-2 text-sm"
                      data-testid="member-edit-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="member-edit-save">
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatChip({ label, value }) {
  return (
    <div className="bg-gray-50 rounded p-2">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
}

function KitPurchaseDialog({ member, outletId, outlets, onClose, onDone }) {
  const { getHeaders, API } = useAuth();
  const [kits, setKits] = useState([]);
  const [selectedKit, setSelectedKit] = useState('');
  const [purchaseOutlet, setPurchaseOutlet] = useState(outletId);
  const [payment, setPayment] = useState({ payment_method: 'cash', payment_reference: '', notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!purchaseOutlet) return;
    axios.get(`${API}/admin/loyalty/kits?outlet_id=${purchaseOutlet}`, { headers: getHeaders() })
      .then((r) => setKits(r.data))
      .catch(() => toast.error('Failed to load kits'));
  }, [API, getHeaders, purchaseOutlet]);

  const selectedKitObj = useMemo(() => kits.find(k => k.id === selectedKit), [kits, selectedKit]);

  const submit = async () => {
    if (!selectedKit) { toast.error('Pick a kit'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/loyalty/kit-purchase`, {
        member_id: member.id,
        kit_id: selectedKit,
        outlet_id: purchaseOutlet,
        payment_method: payment.payment_method,
        payment_reference: payment.payment_reference,
        notes: payment.notes,
        custom_addons: [],
      }, { headers: getHeaders() });
      toast.success('Kit sold and vouchers issued');
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Purchase failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="kit-purchase-dialog">
        <DialogHeader>
          <DialogTitle>Sell Kit to {member.name}</DialogTitle>
          <DialogDescription>
            Records the sale, issues a membership card, and creates all voucher instances.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Purchase Outlet">
            <Select value={purchaseOutlet} onValueChange={(v) => { setPurchaseOutlet(v); setSelectedKit(''); }}>
              <SelectTrigger data-testid="purchase-outlet"><SelectValue /></SelectTrigger>
              <SelectContent>
                {outlets.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Kit">
            <Select value={selectedKit} onValueChange={setSelectedKit}>
              <SelectTrigger data-testid="purchase-kit"><SelectValue placeholder="Select kit" /></SelectTrigger>
              <SelectContent>
                {kits.length === 0 && <div className="p-2 text-xs text-gray-400">No active kits at this outlet</div>}
                {kits.map((k) => (
                  <SelectItem key={k.id} value={k.id}>
                    {k.name} — {INR(k.price)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          {selectedKitObj && (
            <div className="bg-gray-50 rounded p-3 text-xs text-gray-600">
              {(selectedKitObj.voucher_composition || []).reduce((s, l) => s + Number(l.count || 0), 0)} vouchers · Valid {selectedKitObj.validity_days} days
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Payment Method">
              <Select value={payment.payment_method} onValueChange={(v) => setPayment({ ...payment, payment_method: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Reference (optional)"><Input value={payment.payment_reference} onChange={(e) => setPayment({ ...payment, payment_reference: e.target.value })} /></Field>
          </div>
          <Field label="Notes"><Textarea rows={2} value={payment.notes} onChange={(e) => setPayment({ ...payment, notes: e.target.value })} /></Field>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !selectedKit} data-testid="purchase-confirm">
            {saving ? 'Processing…' : 'Sell Kit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
//                          Alerts tab
// ============================================================

function AlertsTab() {
  const { getHeaders, API } = useAuth();
  const { canEdit } = useLoyaltyCaps();
  const [rows, setRows] = useState([]);
  const [reviewedFilter, setReviewedFilter] = useState('open');
  const [severity, setSeverity] = useState('all');

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (reviewedFilter !== 'all') params.append('reviewed', reviewedFilter === 'reviewed' ? 'true' : 'false');
    if (severity !== 'all') params.append('severity', severity);
    try {
      const res = await axios.get(`${API}/admin/loyalty/alerts?${params.toString()}`, { headers: getHeaders() });
      setRows(res.data.alerts || []);
    } catch { toast.error('Failed to load alerts'); }
  }, [API, getHeaders, reviewedFilter, severity]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (a) => {
    const notes = window.prompt('Notes (optional):', a.notes || '') ?? a.notes;
    try {
      await axios.patch(`${API}/admin/loyalty/alerts/${a.id}`,
        { reviewed: !a.reviewed, notes }, { headers: getHeaders() });
      toast.success(a.reviewed ? 'Alert re-opened' : 'Marked reviewed');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  return (
    <div className="space-y-4 mt-4" data-testid="alerts-tab">
      <div className="flex items-center gap-3">
        <Select value={reviewedFilter} onValueChange={setReviewedFilter}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="reviewed">Reviewed</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
        <Select value={severity} onValueChange={setSeverity}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Severity" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All severities</SelectItem>
            <SelectItem value="low">Low</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
          </SelectContent>
        </Select>
        <Button size="sm" variant="ghost" onClick={load}><RefreshCw className="w-4 h-4" /></Button>
      </div>

      {rows.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-gray-500">No alerts.</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {rows.map((a) => (
            <Card key={a.id} data-testid={`alert-${a.id}`}>
              <CardContent className="p-3 flex items-start gap-3">
                <SeverityDot severity={a.severity} />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <div className="font-medium text-sm">{a.message}</div>
                    <Badge variant="outline" className="text-xs">{a.alert_type}</Badge>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {String(a.created_at || '').slice(0, 19).replace('T', ' ')}
                    {a.reviewed && a.reviewed_by && ` · reviewed by ${a.reviewed_by}`}
                  </div>
                  {a.notes && <div className="text-xs text-gray-600 mt-1 italic">{a.notes}</div>}
                </div>
                <Button size="sm" variant={a.reviewed ? 'ghost' : 'outline'} onClick={() => toggle(a)}
                        data-testid={`alert-toggle-${a.id}`}
                        disabled={!canEdit}
                        style={canEdit ? undefined : { display: 'none' }}>
                  {a.reviewed ? 'Re-open' : 'Mark reviewed'}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function SeverityDot({ severity }) {
  const color = { low: 'bg-blue-500', medium: 'bg-amber-500', high: 'bg-rose-500', critical: 'bg-red-700' }[severity] || 'bg-gray-400';
  return (
    <div className="pt-1"><div className={`w-2.5 h-2.5 rounded-full ${color}`} /></div>
  );
}

// ============================================================
//                     Reconciliation tab
// ============================================================

function ReconciliationTab({ outletId, outlets }) {
  const { getHeaders, API } = useAuth();
  const [filters, setFilters] = useState({
    outlet_id: outletId || '', date_from: '', date_to: '', voucher_code: '',
  });
  useEffect(() => { setFilters(f => ({ ...f, outlet_id: outletId || '' })); }, [outletId]);
  const [list, setList] = useState({ count: 0, events: [] });
  const [summary, setSummary] = useState(null);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    try {
      const [l, s] = await Promise.all([
        axios.get(`${API}/admin/loyalty/reconciliation?${params.toString()}`, { headers: getHeaders() }),
        axios.get(`${API}/admin/loyalty/reconciliation/summary?${params.toString()}`, { headers: getHeaders() }),
      ]);
      setList(l.data); setSummary(s.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); }
  }, [API, getHeaders, filters]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4 mt-4" data-testid="reconciliation-tab">
      <Card>
        <CardContent className="p-3 grid grid-cols-2 md:grid-cols-5 gap-2">
          <Field label="Outlet">
            <Select value={filters.outlet_id || 'all'} onValueChange={(v) => setFilters({ ...filters, outlet_id: v === 'all' ? '' : v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All outlets</SelectItem>
                {outlets.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="From"><Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} /></Field>
          <Field label="To"><Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} /></Field>
          <Field label="Voucher Code"><Input value={filters.voucher_code} onChange={(e) => setFilters({ ...filters, voucher_code: e.target.value })} data-testid="recon-voucher-code" /></Field>
          <div className="flex items-end"><Button className="w-full" onClick={load}><RefreshCw className="w-4 h-4 mr-1" />Apply</Button></div>
        </CardContent>
      </Card>

      {summary && (
        <div className="grid grid-cols-3 gap-3">
          <KpiCard label="Redemptions" value={summary.totals.count} testid="recon-count" />
          <KpiCard label="Discount Total" value={INR(summary.totals.discount_total)} testid="recon-discount" />
          <KpiCard label="Bill Total" value={INR(summary.totals.bill_total)} testid="recon-bill" />
        </div>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">Redemption Events ({list.count})</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500 border-b bg-gray-50">
              <tr>
                <th className="text-left p-2">When</th>
                <th className="text-left p-2">Voucher</th>
                <th className="text-left p-2">Type</th>
                <th className="text-left p-2">Member</th>
                <th className="text-left p-2">Bill Ref</th>
                <th className="text-right p-2">Bill</th>
                <th className="text-right p-2">Discount</th>
                <th className="text-left p-2">Redeemed By</th>
              </tr>
            </thead>
            <tbody>
              {list.events.length === 0 && (
                <tr><td colSpan={8} className="p-6 text-center text-gray-400">No redemptions in scope.</td></tr>
              )}
              {list.events.map((e) => (
                <tr key={e.id} className="border-b last:border-b-0 hover:bg-gray-50">
                  <td className="p-2 whitespace-nowrap text-xs">{String(e.redeemed_at || '').slice(0, 19).replace('T', ' ')}</td>
                  <td className="p-2 font-mono text-xs">{e.voucher_code}</td>
                  <td className="p-2">{e.voucher_type_name || e.voucher_type_code}</td>
                  <td className="p-2 text-xs">{e.member_id?.slice(0, 8)}…</td>
                  <td className="p-2">{e.bill_ref || '—'}</td>
                  <td className="p-2 text-right">{INR(e.bill_amount)}</td>
                  <td className="p-2 text-right text-emerald-700">{INR(e.discount_amount)}</td>
                  <td className="p-2 text-xs">{e.redeemed_by_email || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
//  Final closure · Manage Membership dialog
// ══════════════════════════════════════════════════════════════

function ManageMembershipDialog({
  open, onOpenChange, detail, action, setAction,
  reason, setReason, note, setNote, busy, onSubmit,
}) {
  const status = (detail?.status || 'active').toLowerCase();
  const chip =
    status === 'blacklisted' ? 'bg-rose-100 text-rose-800 border-rose-200' :
    status === 'on_hold'     ? 'bg-amber-100 text-amber-800 border-amber-200' :
                               'bg-emerald-100 text-emerald-800 border-emerald-200';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="manage-membership-dialog">
        <DialogHeader>
          <DialogTitle>Manage Membership</DialogTitle>
          <DialogDescription>
            Administrative controls for {detail?.name}. Every action is
            audit-logged with the reason and the actor.
          </DialogDescription>
        </DialogHeader>

        <div className="border rounded p-3 flex items-center justify-between">
          <div className="text-sm">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Current status</div>
            <Badge className={`mt-1 ${chip}`}>
              {status.replace('_', ' ').toUpperCase()}
            </Badge>
          </div>
        </div>

        {!action && (
          <div className="space-y-2 pt-1">
            {status === 'active' && (
              <>
                <Button variant="outline" className="w-full justify-start"
                        onClick={() => setAction('hold')}
                        data-testid="manage-place-on-hold">
                  Place On Hold
                </Button>
                <Button variant="outline" className="w-full justify-start text-rose-700 hover:text-rose-800"
                        onClick={() => setAction('blacklist')}
                        data-testid="manage-blacklist">
                  Blacklist Member
                </Button>
              </>
            )}
            {status === 'on_hold' && (
              <>
                <Button variant="outline" className="w-full justify-start text-emerald-700"
                        onClick={() => setAction('resume')}
                        data-testid="manage-resume">
                  Resume Membership
                </Button>
                <Button variant="outline" className="w-full justify-start text-rose-700"
                        onClick={() => setAction('blacklist')}
                        data-testid="manage-blacklist">
                  Blacklist Member
                </Button>
              </>
            )}
            {status === 'blacklisted' && (
              <Button variant="outline" className="w-full justify-start text-emerald-700"
                      onClick={() => setAction('restore')}
                      data-testid="manage-restore">
                Remove Blacklist / Restore Membership
              </Button>
            )}
          </div>
        )}

        {action && (
          <div className="space-y-3 pt-1">
            <div className={`text-sm rounded p-2 ${
              action === 'blacklist' ? 'bg-rose-50 border border-rose-200 text-rose-800' :
              action === 'restore'   ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' :
              action === 'resume'    ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' :
                                       'bg-amber-50 border border-amber-200 text-amber-800'
            }`}>
              {action === 'hold' && "Member will be placed ON HOLD. Reveal + redemption blocked. Wallet remains viewable."}
              {action === 'resume' && "Member returns to ACTIVE. All benefits become available again."}
              {action === 'blacklist' && "Member is restricted from all benefits. Guest-facing wording is neutral — internal notes stay in the audit log."}
              {action === 'restore' && "Restore full access. Membership returns to ACTIVE."}
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Reason *</Label>
              <Textarea rows={2} value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        data-testid="manage-reason"
                        placeholder="Why is this action being taken?" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Internal note (optional)</Label>
              <Textarea rows={2} value={note}
                        onChange={(e) => setNote(e.target.value)}
                        data-testid="manage-note"
                        placeholder="Private context — never shown to guest or manager." />
            </div>
          </div>
        )}

        <DialogFooter>
          {action ? (
            <>
              <Button variant="ghost" onClick={() => { setAction(null); setReason(''); setNote(''); }}>
                Back
              </Button>
              <Button onClick={onSubmit} disabled={busy}
                      className={
                        action === 'blacklist' ? 'bg-rose-600 hover:bg-rose-700 text-white' :
                                                 'bg-amber-500 hover:bg-amber-600 text-white'
                      }
                      data-testid="manage-confirm-btn">
                {busy ? 'Working…' :
                 action === 'hold'      ? 'Place On Hold'  :
                 action === 'resume'    ? 'Resume'         :
                 action === 'blacklist' ? 'Blacklist'      :
                                          'Restore'}
              </Button>
            </>
          ) : (
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ══════════════════════════════════════════════════════════════
//  Final closure · Voucher Pause / Resume / Extend dialog
// ══════════════════════════════════════════════════════════════

function VoucherActionDialog({
  action, onClose, selectedIds, vouchers,
  busy, reason, setReason,
  extendMode, setExtendMode,
  extendDaysPreset, setExtendDaysPreset,
  extendCustomDate, setExtendCustomDate,
  onSubmit,
}) {
  if (!action) return null;
  const rows = vouchers.filter(v => selectedIds.includes(v.id));
  const title =
    action === 'pause'  ? 'Pause selected vouchers'  :
    action === 'resume' ? 'Resume selected vouchers' :
                          'Extend validity';
  const previewNewExpiry = (v) => {
    if (extendMode === 'days') {
      try {
        const cur = new Date(v.valid_until);
        cur.setDate(cur.getDate() + Number(extendDaysPreset || 0));
        return cur.toISOString().slice(0, 10);
      } catch { return '—'; }
    }
    return extendCustomDate ? extendCustomDate.slice(0, 10) : '—';
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="voucher-action-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {action === 'pause' && 'These vouchers will be temporarily unavailable for Reveal or redemption.'}
            {action === 'resume' && 'Paused vouchers return to normal availability.'}
            {action === 'extend' && "Only the selected voucher instances change. The Voucher Type's master validity is not modified."}
          </DialogDescription>
        </DialogHeader>

        {action === 'extend' && (
          <div className="space-y-2">
            <div className="flex gap-1">
              {[7, 15, 30].map((d) => (
                <Button key={d} size="sm"
                        variant={extendMode === 'days' && extendDaysPreset === d ? 'default' : 'outline'}
                        className="flex-1"
                        onClick={() => { setExtendMode('days'); setExtendDaysPreset(d); }}
                        data-testid={`extend-days-${d}`}>
                  +{d} days
                </Button>
              ))}
              <Button size="sm"
                      variant={extendMode === 'date' ? 'default' : 'outline'}
                      onClick={() => setExtendMode('date')}
                      data-testid="extend-days-custom">
                Custom
              </Button>
            </div>
            {extendMode === 'date' && (
              <Input type="date" value={extendCustomDate}
                     onChange={(e) => setExtendCustomDate(e.target.value)}
                     data-testid="extend-custom-date" />
            )}
          </div>
        )}

        <div className="max-h-40 overflow-y-auto border rounded text-xs divide-y">
          {rows.map((v) => (
            <div key={v.id} className="p-2 flex items-center justify-between">
              <div className="min-w-0">
                <div className="font-mono text-[10px] text-slate-500">{v.voucher_code}</div>
                <div className="truncate">{v.voucher_type_name}</div>
              </div>
              <div className="text-right whitespace-nowrap ml-2">
                <div className="text-[10px] text-slate-500">
                  Now: {String(v.valid_until || '').slice(0, 10)}
                </div>
                {action === 'extend' && (
                  <div className="text-[10px] text-emerald-700 font-medium">
                    → {previewNewExpiry(v)}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-500">Reason *</Label>
          <Textarea rows={2} value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    data-testid="voucher-action-reason"
                    placeholder="Recorded in audit log." />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={onSubmit} disabled={busy || rows.length === 0}
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                  data-testid="voucher-action-confirm-btn">
            {busy ? 'Working…' :
             action === 'pause'  ? `Pause ${rows.length}` :
             action === 'resume' ? `Resume ${rows.length}` :
                                   `Extend ${rows.length}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

